import { useEffect, useState, useRef, useCallback } from "react";
import { Card, SectionHeader } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { useAuth } from "../lib/auth.jsx";
import { bookingDisplayCode } from "../lib/bookingCode.js";
import { DesignDetailModal } from "../components/DesignDetailModal";
import { AvatarUpload } from "../components/AvatarUpload";

/** Shared currency symbol map */
export const CURRENCY_SYMBOLS = {
  USD: '$', CAD: 'CA$', GBP: '£', EUR: '€', NGN: '₦',
  GHS: 'GH₵', ZAR: 'R', KES: 'KSh', AUD: 'A$', NZD: 'NZ$',
  AED: 'د.إ', INR: '₹', SGD: 'S$', MYR: 'RM', JPY: '¥',
  BRL: 'R$', MXN: 'MX$', EGP: 'E£', SAR: '﷼', ILS: '₪',
  CNY: '¥', KRW: '₩', THB: '฿', HKD: 'HK$', IDR: 'Rp',
};

/** Format cents as a currency amount using the studio's configured currency */
function fmtCurrency(cents, currencyCode = 'USD') {
  const code = String(currencyCode || "USD").toUpperCase();
  const sym = CURRENCY_SYMBOLS[code] || code;
  const amount = (Number(cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sym}${amount}`;
}

function convertCurrencyCents(cents, fromCurrency = "USD", toCurrency = "USD", ratesData = null) {
  const from = String(fromCurrency || "USD").toUpperCase();
  const to = String(toCurrency || "USD").toUpperCase();
  const amount = Number(cents || 0);
  if (from === to) return amount;

  const rates = ratesData?.rates || {};
  const fromRate = Number(rates[from] ?? (from === "USD" ? 1 : 0));
  const toRate = Number(rates[to] ?? (to === "USD" ? 1 : 0));
  if (!fromRate || !toRate) return amount;

  return Math.round((amount / fromRate) * toRate);
}

function isPayableInvoice(status = "") {
  return ["pending", "issued"].includes(String(status).toLowerCase());
}

const VENDOR_PLAN_LABELS = {
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semiannual: "Every 6 months",
  annual: "Annual",
};

function formatVendorPlanLabel(interval = "") {
  return VENDOR_PLAN_LABELS[interval] || String(interval || "Plan").replace(/_/g, " ");
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function AdminDashboard() {
  const { authedFetch } = useAuth();
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState([]);
  const [requestView, setRequestView] = useState("active");
  const [selectedReqIds, setSelectedReqIds] = useState(() => new Set());
  const [techs, setTechs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [err, setErr] = useState("");

  // New tech form
  const [newTech, setNewTech] = useState({ email: "", password: "", full_name: "" });
  const [busy, setBusy] = useState(false);

  const [overview, setOverview] = useState(null);
  const [byTech, setByTech]     = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [promos, setPromos]     = useState([]);
  const [newPromo, setNewPromo] = useState({ code: "", description: "", discount_type: "percent", amount: 10 });
  const [editingPromoId, setEditingPromoId] = useState(null);
  const [promoDraft, setPromoDraft] = useState(null);
  const [promoBusy, setPromoBusy] = useState("");
  const [vendorPlans, setVendorPlans] = useState({});
  const [vendorLicenses, setVendorLicenses] = useState([]);
  const [newVendorLicense, setNewVendorLicense] = useState({ studioName: "", label: "", interval: "monthly" });
  const [generatedVendorKey, setGeneratedVendorKey] = useState(null);
  const [vendorBusy, setVendorBusy] = useState(false);

  // Currently-open request in the detail modal.
  const [requestFor, setRequestFor] = useState(null);

  // Tech picker modal state (for visual tech assignment in Requests tab)
  const [showTechPicker, setShowTechPicker] = useState(null);

  // Locations (multi-location admin)
  const [locations, setLocations] = useState([]);
  const [newLocation, setNewLocation] = useState({
    name: "", address: "", city: "", state: "", postal_code: "", country: "US", timezone: "America/New_York",
  });
  const [locBusy, setLocBusy] = useState(false);

  // Studio Settings (currency, country, etc.)
  const [settings, setSettings] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);

  // Exchange rates
  const [ratesData, setRatesData] = useState(null);
  const [ratesRefreshing, setRatesRefreshing] = useState(false);

  const [rotationStatus, setRotationStatus] = useState(null);
  const [rotationHistory, setRotationHistory] = useState([]);
  const [rotationBusy, setRotationBusy] = useState(false);

  // Technician management state
  const [editingTech, setEditingTech] = useState(null);
  const [viewingAnalytics, setViewingAnalytics] = useState(null);
  const [techAnalytics, setTechAnalytics] = useState(null);

  // User management state
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  async function refresh() {
    try {
      const [r, t, b, u, o, bt, a, p, locs, stg, rates, vp, vl] = await Promise.all([
        authedFetch(`/api/admin/requests?view=${requestView}`),
        authedFetch("/api/admin/technicians"),
        authedFetch("/api/admin/bookings"),
        authedFetch("/api/admin/users"),
        authedFetch("/api/reports/overview").catch(() => null),
        authedFetch("/api/reports/by-tech").catch(() => ({ techs: [] })),
        authedFetch("/api/reports/analytics").catch(() => null),
        authedFetch("/api/promotions").catch(() => ({ promotions: [] })),
        authedFetch("/api/catalog/locations?includeInactive=true").catch(() => ({ locations: [] })),
        authedFetch("/api/admin/settings").catch(() => null),
        authedFetch("/api/currency/rates").catch(() => null),
        authedFetch("/api/vendor/plans").catch(() => ({ plans: {} })),
        authedFetch("/api/vendor/licenses").catch(() => ({ licenses: [] })),
      ]);
      setRequests(r.requests || []); setTechs(t.technicians || []); setBookings(b.bookings || []);
      setUsers(u.users || []);
      if (o) setOverview(o);
      if (a) setAnalytics(a);
      setByTech(bt.techs || []); setPromos(p.promotions || []);
      setLocations(locs.locations || []);
      if (stg) setSettings(stg.settings);
      if (rates) setRatesData(rates);
      setVendorPlans(vp?.plans || {});
      setVendorLicenses(vl?.licenses || []);
    } catch (e) { setErr(e.message); }
  }

  async function createLocation(e) {
    e.preventDefault(); setLocBusy(true);
    try {
      await authedFetch("/api/catalog/locations", {
        method: "POST",
        body: JSON.stringify(newLocation),
      });
      setNewLocation({ name: "", address: "", city: "", state: "", postal_code: "", country: "US", timezone: "America/New_York" });
      await refresh();
    } catch (e) { setErr(e.message); }
    setLocBusy(false);
  }

  async function patchLocation(id, patch) {
    try {
      await authedFetch(`/api/catalog/locations/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      await refresh();
    } catch (e) { setErr(e.message); }
  }

  async function deactivateLocation(id) {
    if (!confirm("Archive this location? Existing bookings keep their reference; new ones can't be assigned.")) return;
    try {
      await authedFetch(`/api/catalog/locations/${id}`, { method: "DELETE" });
      await refresh();
    } catch (e) { setErr(e.message); }
  }

  async function createPromo(e) {
    e.preventDefault();
    try {
      await authedFetch("/api/promotions", { method: "POST", body: JSON.stringify(newPromo) });
      setNewPromo({ code: "", description: "", discount_type: "percent", amount: 10 });
      refresh();
    } catch (e) { setErr(e.message); }
  }

  function startEditPromo(promo) {
    setEditingPromoId(promo.id);
    setPromoDraft({
      code: promo.code || "",
      description: promo.description || "",
      discount_type: promo.discount_type || "percent",
      amount: Number(promo.amount || 0),
      min_amount_cents: Number(promo.min_amount_cents || 0),
      max_uses: promo.max_uses || "",
      ends_at: promo.ends_at ? new Date(promo.ends_at).toISOString().slice(0, 10) : "",
      first_booking_only: Boolean(promo.first_booking_only),
      is_active: promo.is_active !== false,
    });
  }

  async function updatePromo(e) {
    e?.preventDefault?.();
    if (!editingPromoId || !promoDraft) return;
    setPromoBusy(editingPromoId);
    try {
      const payload = {
        ...promoDraft,
        max_uses: promoDraft.max_uses === "" ? null : Number(promoDraft.max_uses),
        ends_at: promoDraft.ends_at ? `${promoDraft.ends_at}T23:59:59` : null,
      };
      await authedFetch(`/api/promotions/${editingPromoId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setEditingPromoId(null);
      setPromoDraft(null);
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setPromoBusy("");
    }
  }

  async function togglePromo(promo) {
    setPromoBusy(promo.id);
    try {
      await authedFetch(`/api/promotions/${promo.id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !promo.is_active }),
      });
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setPromoBusy("");
    }
  }

  async function deletePromo(promo) {
    if (!confirm(`Delete promo code ${promo.code}? This cannot be undone.`)) return;
    setPromoBusy(promo.id);
    try {
      await authedFetch(`/api/promotions/${promo.id}`, { method: "DELETE" });
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setPromoBusy("");
    }
  }

  async function createVendorLicense(e) {
    e.preventDefault();
    setVendorBusy(true);
    setGeneratedVendorKey(null);
    try {
      const out = await authedFetch("/api/vendor/licenses", {
        method: "POST",
        body: JSON.stringify(newVendorLicense),
      });
      setGeneratedVendorKey(out);
      setNewVendorLicense({ studioName: "", label: "", interval: newVendorLicense.interval || "monthly" });
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setVendorBusy(false);
    }
  }

  useEffect(() => { refresh(); }, [requestView]);

  useEffect(() => {
    if (tab === "rotation") {
      loadRotationData();
    }
  }, [tab]);

  async function loadRotationData() {
    setRotationBusy(true);
    try {
      const status = await authedFetch("/api/ops/apple-rotation/status");
      const history = await authedFetch("/api/ops/apple-rotation/history");
      setRotationStatus(status);
      setRotationHistory(history.history || []);
      setErr("");
    } catch (e) {
      setErr(e.message);
    } finally {
      setRotationBusy(false);
    }
  }

  async function assign(reqId, technicianId) {
    try {
      await authedFetch(`/api/admin/requests/${reqId}/assign`, {
        method: "POST", body: JSON.stringify({ technicianId }),
      });
      refresh();
    } catch (e) { setErr(e.message); }
  }

  async function archiveRequest(reqId) {
    try {
      await authedFetch(`/api/admin/requests/${reqId}/archive`, {
        method: "POST",
        body: JSON.stringify({ reason: "admin_cleared" }),
      });
      refresh();
    } catch (e) { setErr(e.message); }
  }

  async function restoreRequest(reqId) {
    try {
      await authedFetch(`/api/admin/requests/${reqId}/restore`, { method: "POST" });
      refresh();
    } catch (e) { setErr(e.message); }
  }

  async function deleteRequest(reqId, { force = false } = {}) {
    if (!force && !confirm("Permanently delete this design and its images? This cannot be undone.")) return;
    try {
      const out = await authedFetch(`/api/admin/requests/${reqId}${force ? "?force=true" : ""}`, { method: "DELETE" });
      if (out?.deleted === false && out?.reason === "has_active_bookings") {
        if (confirm(`This design has ${out.bookings} active booking(s). Delete anyway? This also removes those bookings.`)) {
          return deleteRequest(reqId, { force: true });
        }
        return;
      }
      setSelectedReqIds((prev) => { const n = new Set(prev); n.delete(reqId); return n; });
      refresh();
    } catch (e) { setErr(e.message); }
  }

  function toggleSelect(reqId) {
    setSelectedReqIds((prev) => {
      const n = new Set(prev);
      n.has(reqId) ? n.delete(reqId) : n.add(reqId);
      return n;
    });
  }

  async function bulkAction(action) {
    const ids = [...selectedReqIds];
    if (ids.length === 0) return;
    const verb = action === "delete" ? "permanently delete" : action;
    if (!confirm(`${verb[0].toUpperCase() + verb.slice(1)} ${ids.length} selected design${ids.length === 1 ? "" : "s"}?${action === "delete" ? " This cannot be undone." : ""}`)) return;
    try {
      const out = await authedFetch("/api/admin/requests/bulk", {
        method: "POST",
        body: JSON.stringify({ ids, action, force: false }),
      });
      if (action === "delete" && out?.blocked > 0) {
        if (confirm(`${out.blocked} design(s) have active bookings and were skipped. Force-delete them too (also removes their bookings)?`)) {
          const blockedIds = (out.results || []).filter((r) => r.reason === "has_active_bookings").map((r) => r.id);
          await authedFetch("/api/admin/requests/bulk", { method: "POST", body: JSON.stringify({ ids: blockedIds, action: "delete", force: true }) });
        }
      }
      setSelectedReqIds(new Set());
      refresh();
    } catch (e) { setErr(e.message); }
  }

  async function archiveOldRequests() {
    try {
      const out = await authedFetch("/api/admin/requests/archive-old", {
        method: "POST",
        body: JSON.stringify({ days: 60, statuses: ["fulfilled", "cancelled"] }),
      });
      alert(`Archived ${out.archived || 0} old fulfilled/cancelled requests.`);
      refresh();
    } catch (e) { setErr(e.message); }
  }

  async function assignBooking(bookingId, technicianId) {
    try {
      const out = await authedFetch(`/api/admin/bookings/${bookingId}/technician`, {
        method: "PATCH",
        body: JSON.stringify({ technicianId }),
      });
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? out.booking : b)));
      setErr("");
    } catch (e) { setErr(e.message); }
  }

  async function createTech(e) {
    e.preventDefault(); setBusy(true);
    try {
      await authedFetch("/api/admin/technicians", {
        method: "POST", body: JSON.stringify(newTech),
      });
      setNewTech({ email: "", password: "", full_name: "" });
      refresh();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  async function updateTech(techId, updates) {
    try {
      const response = await authedFetch(`/api/admin/technicians/${techId}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      });
      // Update local state
      setTechs(prev => prev.map(t => t.id === techId ? response.technician : t));
      setEditingTech(null);
      setErr("");
    } catch (e) {
      setErr(e.message);
    }
  }

  async function deactivateTech(techId) {
    if (!confirm("Deactivate this technician? They won't be able to log in or take bookings.")) return;
    try {
      await authedFetch(`/api/admin/technicians/${techId}`, { method: "DELETE" });
      refresh();
    } catch (e) {
      setErr(e.message);
    }
  }

  async function loadTechAnalytics(techId, days = 30) {
    setViewingAnalytics(techId);
    try {
      const response = await authedFetch(`/api/admin/technicians/${techId}/analytics?days=${days}`);
      setTechAnalytics(response.analytics);
    } catch (e) {
      setErr(e.message);
      setTechAnalytics(null);
    }
  }

  // User management functions
  async function updateUser(userId, updates) {
    try {
      const response = await authedFetch(`/api/admin/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      });
      setUsers(prev => prev.map(u => u.id === userId ? response.user : u));
      setEditingUser(null);
      setErr("");
    } catch (e) {
      setErr(e.message);
    }
  }

  async function deleteUser(userId) {
    setDeletingUser(true);
    try {
      await authedFetch(`/api/admin/users/${userId}?hard=true`, { method: "DELETE" });
      setDeleteUserTarget(null);
      refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setDeletingUser(false);
    }
  }

  async function resetUserPassword(userId) {
    const newPassword = prompt("Enter new password for this user (min 8 characters):");
    if (!newPassword) return;
    if (newPassword.length < 8) {
      alert("Password must be at least 8 characters");
      return;
    }
    try {
      await authedFetch(`/api/admin/users/${userId}/reset-password`, {
        method: "POST",
        body: JSON.stringify({ newPassword }),
      });
      alert("Password updated successfully");
    } catch (e) {
      setErr(e.message);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "30px 16px" }}>
        <SectionHeader emoji="👑" title="Admin Console" subtitle="Assign requests, manage technicians, oversee bookings" />

        <div style={{ display: "flex", gap: 8, marginBottom: 20, justifyContent: "center", flexWrap: "wrap" }}>
          {[
            ["book",       "📅 Book for Customer"],
            ["gallery",    "🖼️ Gallery"],
            ["invoices",   "💰 Invoices"],
            ["requests",   `Requests (${requests.length})`],
            ["technicians",`Technicians (${techs.length})`],
            ["users",      `👥 Users (${users.length})`],
            ["bookings",   `Bookings (${bookings.length})`],
            ["inProgress", `In Progress (${bookings.filter((b) => b.status === "in_progress").length})`],
            ["locations",  `Locations (${locations.filter((l) => l.is_active).length})`],
            ["settings",   "⚙️ Settings"],
            ["rotation",   "🍎 Apple rotation"],
            ["reports",    "Reports"],
            ["vendors",    "Vendor access"],
            ["promos",     `Promo codes (${promos.length})`],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{
              padding: "8px 16px", borderRadius: 999, fontSize: 13, fontWeight: 600,
              background: tab === k ? "linear-gradient(135deg, var(--mauve), var(--mauve-deep))" : "var(--bg-elevated)",
              color: tab === k ? "#fff" : "var(--text)",
              border: tab === k ? "none" : "1.5px solid var(--border)",
              cursor: "pointer",
            }}>{l}</button>
          ))}
        </div>

        {tab === "requests" && (
          <div style={{ display: "grid", gap: 12 }}>
            <Card style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  ["active", "Active"],
                  ["archived", "Archived"],
                  ["all", "All"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => setRequestView(value)}
                    style={{
                      padding: "7px 12px",
                      borderRadius: 999,
                      border: requestView === value ? "1.5px solid var(--mauve)" : "1px solid var(--border)",
                      background: requestView === value ? "rgba(196,135,154,0.12)" : "var(--bg)",
                      color: "var(--text)",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {requests.length > 0 && (
                  <Button size="sm" variant="ghost"
                    label={selectedReqIds.size === requests.length ? "Clear selection" : "Select all"}
                    onClick={() => setSelectedReqIds(selectedReqIds.size === requests.length ? new Set() : new Set(requests.map((r) => r.id)))} />
                )}
                <Button size="sm" label="Auto-archive old completed" variant="outline" onClick={archiveOldRequests} />
              </div>
            </Card>
            {selectedReqIds.size > 0 && (
              <Card style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", background: "rgba(196,135,154,0.08)", border: "1px solid var(--border-strong)" }}>
                <span style={{ fontWeight: 700, color: "var(--text)" }}>{selectedReqIds.size} selected</span>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button size="sm" variant="outline" label="Archive selected" onClick={() => bulkAction("archive")} />
                  {requestView === "archived" && <Button size="sm" variant="outline" label="Restore selected" onClick={() => bulkAction("restore")} />}
                  <Button size="sm" variant="danger" label="Delete permanently" onClick={() => bulkAction("delete")} />
                  <Button size="sm" variant="ghost" label="Clear" onClick={() => setSelectedReqIds(new Set())} />
                </div>
              </Card>
            )}
            {requests.length === 0 && <Card><div style={{ textAlign: "center", color: "var(--text-muted)" }}>No requests yet.</div></Card>}
            {requests.map((r) => {
              const designName = r.final_design?.name;
              const optionCount = Array.isArray(r.generated) ? r.generated.length : 0;
              const assignedTech = techs.find(t => t.id === r.assigned_to);
              const techShare = Number(assignedTech?.technician?.commission_pct ?? 80);
              const studioShare = Math.max(0, 100 - Math.min(Math.max(techShare, 0), 100));
              return (
                <article
                  key={r.id}
                  // Whole row is clickable to open details. The assign select
                  // calls stopPropagation so changing it doesn't also pop the
                  // modal.
                  onClick={() => setRequestFor(r)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setRequestFor(r);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`View request from ${r.customer?.full_name || r.customer?.email || "customer"}`}
                  style={{
                    background: "var(--bg-elevated)", borderRadius: 14, padding: 16,
                    border: "1px solid var(--border)", display: "flex", justifyContent: "space-between",
                    gap: 12, alignItems: "center", flexWrap: "wrap",
                    cursor: "pointer",
                    transition: "transform .18s var(--ease-out), box-shadow .18s var(--ease-out), border-color .18s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 8px 22px rgba(196,135,154,0.18)";
                    e.currentTarget.style.borderColor = "var(--border-strong)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = "";
                    e.currentTarget.style.borderColor = "var(--border)";
                  }}
                >
                  <div style={{ display: "flex", gap: 12, alignItems: "center", flex: "1 1 240px", minWidth: 200 }}>
                    <input
                      type="checkbox"
                      checked={selectedReqIds.has(r.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { e.stopPropagation(); toggleSelect(r.id); }}
                      aria-label="Select design"
                      style={{ width: 18, height: 18, flexShrink: 0, cursor: "pointer" }}
                    />
                    {r.final_design?.image_url && (
                      <img
                        src={r.final_design.image_url}
                        alt=""
                        style={{ width: 48, height: 48, borderRadius: 10, objectFit: "cover", flexShrink: 0 }}
                      />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
                        {r.customer?.full_name || r.customer?.email}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {r.category} · {new Date(r.created_at).toLocaleString()} · status: {r.status}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--accent)", fontWeight: 600, marginTop: 2 }}>
                        {designName
                          ? `Picked: ${designName}`
                          : optionCount > 0
                            ? `${optionCount} option${optionCount === 1 ? "" : "s"} generated · awaiting pick`
                            : "No designs generated yet"}
                      </div>
                      {r.assigned_to && (
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                          Split: tech keeps {techShare}% · studio commission {studioShare}% · tips go to tech
                        </div>
                      )}
                      {r.archived_at && (
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                          Archived {new Date(r.archived_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {/* Visual tech assignment - opens tech picker on click */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTechPicker(r.id);
                      }}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 10,
                        border: "1.5px solid var(--border)",
                        background: r.assigned_to ? "rgba(196,135,154,0.10)" : "var(--bg)",
                        color: "var(--text)",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.15s ease"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "var(--mauve)";
                        e.currentTarget.style.background = "rgba(196,135,154,0.10)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "var(--border)";
                        e.currentTarget.style.background = r.assigned_to ? "rgba(196,135,154,0.10)" : "var(--bg)";
                      }}
                    >
                      {r.assigned_to
                        ? (assignedTech?.full_name || "Assigned")
                        : "Assign technician…"}
                    </button>
                    {r.archived_at ? (
                      <Button
                        size="sm"
                        label="Restore"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          restoreRequest(r.id);
                        }}
                      />
                    ) : (
                      <Button
                        size="sm"
                        label="Archive"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          archiveRequest(r.id);
                        }}
                      />
                    )}
                    <Button
                      size="sm"
                      label="Delete"
                      variant="danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteRequest(r.id);
                      }}
                    />
                    <span aria-hidden="true" style={{ color: "var(--accent)", fontWeight: 700, fontSize: 13 }}>View →</span>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {tab === "technicians" && (
          <>
            <Card style={{ marginBottom: 20 }}>
              <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Add a technician</h3>
              <form onSubmit={createTech} style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", alignItems: "flex-end" }}>
                <Input label="Full Name" required value={newTech.full_name} onChange={(e) => setNewTech({ ...newTech, full_name: e.target.value })} />
                <Input label="Email" required type="email" value={newTech.email} onChange={(e) => setNewTech({ ...newTech, email: e.target.value })} />
                <Input label="Temp Password" required type="text" value={newTech.password} onChange={(e) => setNewTech({ ...newTech, password: e.target.value })} />
                <Button type="submit" label="Create" icon="+" loading={busy} />
              </form>
            </Card>

            <div style={{ display: "grid", gap: 14 }}>
              {techs.map((t) => {
                const tech = t.technician || {};
                const isEditing = editingTech === t.id;
                const isActive = tech.is_active !== false;

                return (
                  <Card key={t.id} style={{ padding: 0, overflow: "hidden", maxWidth: "none", margin: 0 }}>
                    <div style={{ padding: 16, display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
                      {/* Avatar Section */}
                      <div style={{ flexShrink: 0 }}>
                        {isEditing ? (
                          <AvatarUpload
                            currentAvatar={t.avatar_url}
                            userId={t.id}
                            size={80}
                            onSuccess={(avatarUrl) => {
                              setTechs(prev => prev.map(tech =>
                                tech.id === t.id ? { ...tech, avatar_url: avatarUrl } : tech
                              ));
                            }}
                          />
                        ) : t.avatar_url ? (
                          <img
                            src={t.avatar_url}
                            alt={t.full_name || t.email}
                            style={{
                              width: 80,
                              height: 80,
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "3px solid var(--border-strong)"
                            }}
                          />
                        ) : (
                          <div style={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 32,
                            border: "3px solid var(--border-strong)"
                          }}>
                            {(t.full_name || t.email || "?")[0].toUpperCase()}
                          </div>
                        )}
                      </div>

                      {/* Info Section */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {isEditing ? (
                          <div style={{ display: "grid", gap: 12 }}>
                            <Input
                              label="Full Name"
                              value={t.full_name || ""}
                              onChange={(e) => setTechs(prev => prev.map(tech =>
                                tech.id === t.id ? { ...tech, full_name: e.target.value } : tech
                              ))}
                            />
                            <Input
                              label="Phone"
                              value={t.phone || ""}
                              onChange={(e) => setTechs(prev => prev.map(tech =>
                                tech.id === t.id ? { ...tech, phone: e.target.value } : tech
                              ))}
                            />
                            <div>
                              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Bio</label>
                              <textarea
                                value={tech.bio || ""}
                                onChange={(e) => setTechs(prev => prev.map(tech =>
                                  tech.id === t.id ? { ...tech, technician: { ...tech.technician, bio: e.target.value } } : tech
                                ))}
                                rows={3}
                                style={{
                                  width: "100%",
                                  padding: "10px 14px",
                                  borderRadius: 8,
                                  border: "1px solid var(--border-strong)",
                                  background: "var(--bg)",
                                  color: "var(--text)",
                                  fontSize: 14,
                                  fontFamily: "inherit",
                                  resize: "vertical"
                                }}
                                placeholder="Expert nail technician with 10 years experience..."
                              />
                            </div>
                            <Input
                              label="Specialties (comma-separated)"
                              value={typeof t._specialtiesText === "string"
                                ? t._specialtiesText
                                : (Array.isArray(tech.specialties) ? tech.specialties.join(", ") : "")}
                              onChange={(e) => setTechs(prev => prev.map(tt =>
                                tt.id === t.id
                                  ? { ...tt, _specialtiesText: e.target.value }
                                  : tt
                              ))}
                              placeholder="Gel Nails, Nail Art, Pedicures"
                            />
                            <Input
                              label="Hourly Rate ($)"
                              type="number"
                              step="0.01"
                              value={tech.hourly_rate_cents ? (tech.hourly_rate_cents / 100).toFixed(2) : ""}
                              onChange={(e) => setTechs(prev => prev.map(tech =>
                                tech.id === t.id ? {
                                  ...tech,
                                  technician: {
                                    ...tech.technician,
                                    hourly_rate_cents: Math.round(parseFloat(e.target.value || 0) * 100)
                                  }
                                } : tech
                              ))}
                            />
                            <div>
                              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Role</label>
                              <select
                                value={t.role || "customer"}
                                onChange={(e) => setTechs(prev => prev.map(tech =>
                                  tech.id === t.id ? { ...tech, role: e.target.value } : tech
                                ))}
                                style={{
                                  width: "100%",
                                  padding: "10px 14px",
                                  borderRadius: 8,
                                  border: "1px solid var(--border-strong)",
                                  background: "var(--bg)",
                                  color: "var(--text)",
                                  fontSize: 14
                                }}
                              >
                                <option value="customer">Customer</option>
                                <option value="technician">Technician</option>
                                <option value="admin_tech">Admin Technician</option>
                              </select>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                              <h3 style={{ fontWeight: 700, fontSize: 18, color: "var(--text)", margin: 0 }}>
                                {t.full_name || t.email}
                              </h3>
                              <span style={{
                                fontSize: 11,
                                fontWeight: 700,
                                padding: "3px 10px",
                                borderRadius: 999,
                                background: isActive ? "rgba(76,201,133,0.16)" : "rgba(200,30,30,0.16)",
                                color: isActive ? "#4CC985" : "#C81E1E"
                              }}>
                                {isActive ? "Active" : "Inactive"}
                              </span>
                              <span style={{
                                fontSize: 11,
                                fontWeight: 700,
                                padding: "3px 10px",
                                borderRadius: 999,
                                background: "rgba(196,135,154,0.16)",
                                color: "var(--mauve)"
                              }}>
                                {t.role || "customer"}
                              </span>
                            </div>
                            <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 4 }}>
                              {t.email}
                              {t.phone && ` · ${t.phone}`}
                            </div>
                            {tech.bio && (
                              <p style={{ fontSize: 14, color: "var(--text)", marginTop: 8, marginBottom: 8, lineHeight: 1.5 }}>
                                {tech.bio}
                              </p>
                            )}
                            {tech.specialties && tech.specialties.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                                {tech.specialties.map((spec, i) => (
                                  <span key={i} style={{
                                    fontSize: 11,
                                    padding: "4px 10px",
                                    borderRadius: 999,
                                    background: "var(--bg-soft)",
                                    color: "var(--text-muted)",
                                    border: "1px solid var(--border)"
                                  }}>
                                    {spec}
                                  </span>
                                ))}
                              </div>
                            )}
                            {tech.hourly_rate_cents && (
                              <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 8 }}>
                                Hourly Rate: {fmtCurrency(tech.hourly_rate_cents, settings?.currency_code)}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Actions Section */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => {
                                // Convert the raw specialties string to an array on save
                                const specialtiesArr = typeof t._specialtiesText === "string"
                                  ? t._specialtiesText.split(",").map(s => s.trim()).filter(Boolean)
                                  : (Array.isArray(tech.specialties) ? tech.specialties : []);
                                updateTech(t.id, {
                                  full_name: t.full_name,
                                  phone: t.phone,
                                  bio: tech.bio,
                                  specialties: specialtiesArr,
                                  hourly_rate_cents: tech.hourly_rate_cents,
                                  role: t.role
                                });
                              }}
                              style={{
                                padding: "8px 16px",
                                borderRadius: 8,
                                fontWeight: 600,
                                fontSize: 13,
                                background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                                color: "#fff",
                                border: "none",
                                cursor: "pointer"
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingTech(null);
                                // Discard the raw specialties draft string
                                setTechs(prev => prev.map(tt =>
                                  tt.id === t.id ? { ...tt, _specialtiesText: undefined } : tt
                                ));
                                refresh();
                              }}
                              style={{
                                padding: "8px 16px",
                                borderRadius: 8,
                                fontWeight: 600,
                                fontSize: 13,
                                background: "var(--bg-soft)",
                                color: "var(--text-muted)",
                                border: "1px solid var(--border)",
                                cursor: "pointer"
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => setEditingTech(t.id)}
                              style={{
                                padding: "8px 16px",
                                borderRadius: 8,
                                fontWeight: 600,
                                fontSize: 13,
                                background: "var(--bg-elevated)",
                                color: "var(--text)",
                                border: "1px solid var(--border-strong)",
                                cursor: "pointer"
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => loadTechAnalytics(t.id)}
                              style={{
                                padding: "8px 16px",
                                borderRadius: 8,
                                fontWeight: 600,
                                fontSize: 13,
                                background: "var(--bg-elevated)",
                                color: "var(--text)",
                                border: "1px solid var(--border-strong)",
                                cursor: "pointer"
                              }}
                            >
                              Analytics
                            </button>
                            <button
                              onClick={() => deactivateTech(t.id)}
                              style={{
                                padding: "8px 16px",
                                borderRadius: 8,
                                fontWeight: 600,
                                fontSize: 13,
                                background: "rgba(200,30,30,0.1)",
                                color: "#C81E1E",
                                border: "1px solid rgba(200,30,30,0.3)",
                                cursor: "pointer"
                              }}
                            >
                              Deactivate
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Analytics Modal */}
            {viewingAnalytics && techAnalytics && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0,0,0,0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                  padding: 16
                }}
                onClick={() => {
                  setViewingAnalytics(null);
                  setTechAnalytics(null);
                }}
              >
                <Card
                  style={{
                    maxWidth: 600,
                    width: "100%",
                    maxHeight: "90vh",
                    overflow: "auto",
                    margin: 0
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                    <h2 style={{ fontFamily: "var(--font-display)", color: "var(--text)", margin: 0 }}>
                      Technician Analytics
                    </h2>
                    <button
                      onClick={() => {
                        setViewingAnalytics(null);
                        setTechAnalytics(null);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: 24,
                        cursor: "pointer",
                        color: "var(--text-muted)"
                      }}
                    >
                      ×
                    </button>
                  </div>

                  <div style={{ display: "grid", gap: 16 }}>
                    {/* Revenue Stats */}
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                      gap: 12
                    }}>
                      <div style={{
                        padding: 16,
                        background: "var(--bg-soft)",
                        borderRadius: 12,
                        border: "1px solid var(--border)"
                      }}>
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>Revenue</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: "var(--text)" }}>
                          {fmtCurrency(techAnalytics.revenueCents, settings?.currency_code)}
                        </div>
                      </div>
                      <div style={{
                        padding: 16,
                        background: "var(--bg-soft)",
                        borderRadius: 12,
                        border: "1px solid var(--border)"
                      }}>
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>Tips</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: "var(--text)" }}>
                          {fmtCurrency(techAnalytics.tipsCents, settings?.currency_code)}
                        </div>
                      </div>
                    </div>

                    {/* Booking Stats */}
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                      gap: 12
                    }}>
                      <div style={{
                        padding: 12,
                        background: "var(--bg-soft)",
                        borderRadius: 12,
                        border: "1px solid var(--border)",
                        textAlign: "center"
                      }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: "var(--text)" }}>
                          {techAnalytics.bookingCount}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Total</div>
                      </div>
                      <div style={{
                        padding: 12,
                        background: "var(--bg-soft)",
                        borderRadius: 12,
                        border: "1px solid var(--border)",
                        textAlign: "center"
                      }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: "#4CC985" }}>
                          {techAnalytics.completedCount}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Completed</div>
                      </div>
                      <div style={{
                        padding: 12,
                        background: "var(--bg-soft)",
                        borderRadius: 12,
                        border: "1px solid var(--border)",
                        textAlign: "center"
                      }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: "#C81E1E" }}>
                          {techAnalytics.cancelledCount}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Cancelled</div>
                      </div>
                    </div>

                    {/* Rating */}
                    <div style={{
                      padding: 16,
                      background: "linear-gradient(135deg, rgba(196,135,154,0.1), rgba(196,135,154,0.05))",
                      borderRadius: 12,
                      border: "1px solid var(--border)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: 36, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>
                        {techAnalytics.avgRating.toFixed(1)} ⭐
                      </div>
                      <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                        {techAnalytics.reviewCount} {techAnalytics.reviewCount === 1 ? "review" : "reviews"}
                      </div>
                    </div>

                    {/* Recent Reviews */}
                    {techAnalytics.recentReviews && techAnalytics.recentReviews.length > 0 && (
                      <div>
                        <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 12 }}>
                          Recent Reviews
                        </h3>
                        <div style={{ display: "grid", gap: 10 }}>
                          {techAnalytics.recentReviews.map((review, i) => (
                            <div key={i} style={{
                              padding: 12,
                              background: "var(--bg-soft)",
                              borderRadius: 8,
                              border: "1px solid var(--border)"
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
                                  {review.customer?.full_name || "Customer"}
                                </div>
                                <div style={{ fontSize: 13, color: "#FFB800" }}>
                                  {"⭐".repeat(review.rating)}
                                </div>
                              </div>
                              {review.comment && (
                                <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
                                  {review.comment}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            )}
          </>
        )}

        {tab === "users" && (
          <>
            <Card style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>
                    Search
                  </label>
                  <input
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    placeholder="Search by name or email..."
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: "1px solid var(--border-strong)",
                      background: "var(--bg)",
                      color: "var(--text)",
                      fontSize: 14
                    }}
                  />
                </div>
                <div style={{ minWidth: 150 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>
                    Filter by Role
                  </label>
                  <select
                    value={userRoleFilter}
                    onChange={(e) => setUserRoleFilter(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: "1px solid var(--border-strong)",
                      background: "var(--bg)",
                      color: "var(--text)",
                      fontSize: 14
                    }}
                  >
                    <option value="all">All Roles</option>
                    <option value="customer">Customer</option>
                    <option value="technician">Technician</option>
                    <option value="admin_tech">Admin</option>
                  </select>
                </div>
              </div>
            </Card>

            <div style={{ display: "grid", gap: 14 }}>
              {users
                .filter(u => {
                  const matchesSearch = !userSearchTerm ||
                    u.full_name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                    u.email?.toLowerCase().includes(userSearchTerm.toLowerCase());
                  const matchesRole = userRoleFilter === "all" || u.role === userRoleFilter;
                  return matchesSearch && matchesRole;
                })
                .map((u) => {
                  const isEditing = editingUser === u.id;
                  return (
                    <Card key={u.id} style={{ padding: 0, overflow: "hidden", maxWidth: "none", margin: 0 }}>
                      <div style={{ padding: 16, display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
                        {/* Avatar Section */}
                        <div style={{ flexShrink: 0 }}>
                          {u.avatar_url ? (
                            <img
                              src={u.avatar_url}
                              alt={u.full_name || u.email}
                              style={{
                                width: 60,
                                height: 60,
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: "2px solid var(--border)"
                              }}
                            />
                          ) : (
                            <div style={{
                              width: 60,
                              height: 60,
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: 24,
                              border: "2px solid var(--border)"
                            }}>
                              {(u.full_name || u.email || "?")[0].toUpperCase()}
                            </div>
                          )}
                        </div>

                        {/* Info Section */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {isEditing ? (
                            <div style={{ display: "grid", gap: 12 }}>
                              <Input
                                label="Full Name"
                                value={u.full_name || ""}
                                onChange={(e) => setUsers(prev => prev.map(user =>
                                  user.id === u.id ? { ...user, full_name: e.target.value } : user
                                ))}
                              />
                              <Input
                                label="Phone"
                                value={u.phone || ""}
                                onChange={(e) => setUsers(prev => prev.map(user =>
                                  user.id === u.id ? { ...user, phone: e.target.value } : user
                                ))}
                              />
                              <div>
                                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>
                                  Role
                                </label>
                                <select
                                  value={u.role || "customer"}
                                  onChange={(e) => setUsers(prev => prev.map(user =>
                                    user.id === u.id ? { ...user, role: e.target.value } : user
                                  ))}
                                  style={{
                                    width: "100%",
                                    padding: "10px 14px",
                                    borderRadius: 8,
                                    border: "1px solid var(--border-strong)",
                                    background: "var(--bg)",
                                    color: "var(--text)",
                                    fontSize: 14
                                  }}
                                >
                                  <option value="customer">Customer</option>
                                  <option value="technician">Technician</option>
                                  <option value="admin_tech">Admin Technician</option>
                                </select>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                                <h3 style={{ fontWeight: 700, fontSize: 16, color: "var(--text)", margin: 0 }}>
                                  {u.full_name || "No name"}
                                </h3>
                                <span style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  padding: "3px 10px",
                                  borderRadius: 999,
                                  background: u.role === "admin_tech" ? "rgba(201,76,76,0.16)" : u.role === "technician" ? "rgba(76,201,133,0.16)" : "rgba(196,135,154,0.16)",
                                  color: u.role === "admin_tech" ? "#C94C4C" : u.role === "technician" ? "#4CC985" : "var(--mauve)"
                                }}>
                                  {u.role || "customer"}
                                </span>
                              </div>
                              <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 4 }}>
                                {u.email}
                                {u.phone && ` · ${u.phone}`}
                              </div>
                              {u.technician && (
                                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
                                  {u.technician.is_active === false && (
                                    <span style={{
                                      padding: "2px 8px",
                                      borderRadius: 999,
                                      background: "rgba(200,30,30,0.12)",
                                      color: "#C81E1E"
                                    }}>
                                      Inactive Technician
                                    </span>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Actions Section */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
                          {isEditing ? (
                            <>
                              <button
                                onClick={() => {
                                  updateUser(u.id, {
                                    full_name: u.full_name,
                                    phone: u.phone,
                                    role: u.role
                                  });
                                }}
                                style={{
                                  padding: "8px 16px",
                                  borderRadius: 8,
                                  fontWeight: 600,
                                  fontSize: 13,
                                  background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                                  color: "#fff",
                                  border: "none",
                                  cursor: "pointer"
                                }}
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingUser(null);
                                  refresh();
                                }}
                                style={{
                                  padding: "8px 16px",
                                  borderRadius: 8,
                                  fontWeight: 600,
                                  fontSize: 13,
                                  background: "var(--bg-soft)",
                                  color: "var(--text-muted)",
                                  border: "1px solid var(--border)",
                                  cursor: "pointer"
                                }}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => setEditingUser(u.id)}
                                style={{
                                  padding: "8px 16px",
                                  borderRadius: 8,
                                  fontWeight: 600,
                                  fontSize: 13,
                                  background: "var(--bg-elevated)",
                                  color: "var(--text)",
                                  border: "1px solid var(--border-strong)",
                                  cursor: "pointer"
                                }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => resetUserPassword(u.id)}
                                style={{
                                  padding: "8px 16px",
                                  borderRadius: 8,
                                  fontWeight: 600,
                                  fontSize: 13,
                                  background: "var(--bg-elevated)",
                                  color: "var(--text)",
                                  border: "1px solid var(--border-strong)",
                                  cursor: "pointer"
                                }}
                              >
                                Reset Password
                              </button>
                              <button
                                onClick={() => setDeleteUserTarget(u)}
                                style={{
                                  padding: "8px 16px",
                                  borderRadius: 8,
                                  fontWeight: 600,
                                  fontSize: 13,
                                  background: "rgba(200,30,30,0.1)",
                                  color: "#C81E1E",
                                  border: "1px solid rgba(200,30,30,0.3)",
                                  cursor: "pointer"
                                }}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
            </div>
          </>
        )}

        {tab === "bookings" && (
          <AdminBookingsBoard bookings={bookings} techs={techs} onAssign={assignBooking}
            authedFetch={authedFetch} onRefresh={refresh} />
        )}

        {tab === "inProgress" && (
          <AdminBookingsBoard
            bookings={bookings.filter((b) => b.status === "in_progress")}
            techs={techs}
            onAssign={assignBooking}
            authedFetch={authedFetch} onRefresh={refresh}
            emptyText="No appointments are in progress."
          />
        )}

        {tab === "locations" && (
          <>
            <Card style={{ marginBottom: 20, maxWidth: "100%" }}>
              <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 4 }}>
                Add a location
              </h3>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
                Each technician can be assigned to a location. Customers see active locations in the booking flow.
              </p>
              <form onSubmit={createLocation} style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", alignItems: "flex-end" }}>
                <Input label="Name"        required value={newLocation.name}        onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })} />
                <Input label="Address"             value={newLocation.address}     onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })} />
                <Input label="City"                value={newLocation.city}        onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })} />
                <Input label="State / region"      value={newLocation.state}       onChange={(e) => setNewLocation({ ...newLocation, state: e.target.value })} />
                <Input label="Postal code"         value={newLocation.postal_code} onChange={(e) => setNewLocation({ ...newLocation, postal_code: e.target.value })} />
                <Input label="Country"             value={newLocation.country}     onChange={(e) => setNewLocation({ ...newLocation, country: e.target.value })} />
                <Input label="Timezone"            value={newLocation.timezone}    onChange={(e) => setNewLocation({ ...newLocation, timezone: e.target.value })} />
                <div style={{ alignSelf: "stretch", display: "flex", alignItems: "flex-end" }}>
                  <Button type="submit" label="Add location" icon="+" loading={locBusy} full />
                </div>
              </form>
            </Card>

            <div style={{ display: "grid", gap: 10 }}>
              {locations.length === 0 ? (
                <Card style={{ maxWidth: "100%" }}>
                  <div style={{ textAlign: "center", color: "var(--text-muted)" }}>No locations yet — add the first one above.</div>
                </Card>
              ) : (
                locations.map((l) => (
                  <LocationRow
                    key={l.id}
                    location={l}
                    onPatch={(patch) => patchLocation(l.id, patch)}
                    onDeactivate={() => deactivateLocation(l.id)}
                  />
                ))
              )}
            </div>
          </>
        )}

        {tab === "reports" && (
          <div style={{ display: "grid", gap: 14 }}>
            {/* ── Exchange Rates Widget ─────────────────────────────────── */}
            <ExchangeRatesWidget
              ratesData={ratesData}
              studioCurrency={settings?.currency_code || "USD"}
              refreshing={ratesRefreshing}
              onRefresh={async () => {
                setRatesRefreshing(true);
                try {
                  const r = await authedFetch("/api/currency/refresh", { method: "POST" });
                  if (r.ok !== false) {
                    const fresh = await authedFetch("/api/currency/rates");
                    setRatesData(fresh);
                  }
                } catch (_) {}
                setRatesRefreshing(false);
              }}
            />

            {/* ── Advanced accounting breakdown ────────────────────────── */}
            {analytics && (
              <AdvancedReports
                analytics={analytics}
                currencyCode={settings?.currency_code}
              />
            )}

            {/* ── 30-day overview ──────────────────────────────────────── */}
            {overview && (
              <Card style={{ maxWidth: "100%" }}>
                <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>
                  Last 30 days
                </h3>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                  <Button variant="outline" label="Share report" onClick={() => shareReport(overview, byTech, settings?.currency_code)} style={{ fontSize: 12, padding: "7px 12px" }} />
                  <Button variant="outline" label="Export CSV" onClick={() => downloadReportCsv(overview, byTech)} style={{ fontSize: 12, padding: "7px 12px" }} />
                  <Button variant="outline" label="Print / PDF" onClick={() => window.print()} style={{ fontSize: 12, padding: "7px 12px" }} />
                </div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                  gap: 12,
                  width: "100%",
                }}>
                  <RStat label="Revenue"   value={fmtCurrency(overview.revenueCents, settings?.currency_code)} accent />
                  <RStat label="≈ USD"     value={overview.revenueUsdCents != null ? fmtCurrency(overview.revenueUsdCents, "USD") : "—"} />
                  <RStat label="Bookings"  value={overview.bookingCount} />
                  <RStat label="Completed" value={overview.completedCount} />
                  <RStat label="Cancelled" value={overview.cancelledCount} />
                  <RStat label="No-shows"  value={overview.noShowCount} />
                  <RStat label="Customers" value={overview.uniqueCustomers} />
                  <RStat label="Repeat"    value={overview.repeatCustomers} />
                </div>
                {/* Conversion footnote */}
                {overview.conversionMeta && overview.conversionMeta.target !== "USD" && (
                  <div style={{ marginTop: 10, fontSize: 11, color: "var(--text-muted)", borderTop: "1px solid var(--border)", paddingTop: 8 }}>
                    Rate used: 1 USD = {overview.conversionMeta.rateUsdPerTarget?.toFixed(4)} {overview.conversionMeta.target} · {overview.conversionMeta.rateDate} · {overview.conversionMeta.source?.split(" ")[0]}
                  </div>
                )}
                {overview.popularCategories?.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>
                      Popular categories
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {overview.popularCategories.map((c) => (
                        <span key={c.category} style={{ fontSize: 12, padding: "4px 12px", borderRadius: 999, background: "var(--bg-soft)", color: "var(--text)" }}>
                          {c.category} · {c.count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* ── By technician ────────────────────────────────────────── */}
            <Card style={{ maxWidth: "100%" }}>
              <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>By technician</h3>
              {byTech.length === 0 ? <div style={{ color: "var(--text-muted)" }}>No data yet.</div> : (
                <>
                  <div style={{ display: "grid", gap: 8 }}>
                    {byTech.map((t) => (
                      <div key={t.technician_id} style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--border)", padding: "8px 0", flexWrap: "wrap", gap: 8 }}>
                        <span style={{ color: "var(--text)", fontWeight: 600 }}>{t.name || t.technician_id?.slice(0,8)}</span>
                        <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
                          {fmtCurrency(t.gross, settings?.currency_code)}
                          {t.grossUsd != null && settings?.currency_code !== "USD" && (
                            <span style={{ color: "var(--text-muted)", opacity: 0.7 }}> (≈{fmtCurrency(t.grossUsd, "USD")})</span>
                          )}
                          {" · "}{t.count} bookings · {t.noShows} no-shows
                        </span>
                      </div>
                    ))}
                  </div>
                  {byTech[0]?.grossUsd != null && settings?.currency_code !== "USD" && (
                    <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-muted)" }}>
                      USD equivalents shown in brackets use the mid-market rate from {byTech[0]?.rateDate || "today"}.
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>
        )}

        {tab === "vendors" && (
          <>
            <Card style={{ marginBottom: 16 }}>
              <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 6 }}>
                Vendor subscription access
              </h3>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 14 }}>
                Generate a time-limited key for another studio, or let vendors subscribe from their profile.
              </p>
              <form onSubmit={createVendorLicense} style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", alignItems: "flex-end" }}>
                <Input
                  label="Studio name"
                  required
                  value={newVendorLicense.studioName}
                  onChange={(e) => setNewVendorLicense({ ...newVendorLicense, studioName: e.target.value })}
                />
                <Input
                  label="Internal label"
                  value={newVendorLicense.label}
                  onChange={(e) => setNewVendorLicense({ ...newVendorLicense, label: e.target.value })}
                />
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:1.4, color:"var(--accent)", marginBottom:6 }}>Duration</label>
                  <select
                    value={newVendorLicense.interval}
                    onChange={(e) => setNewVendorLicense({ ...newVendorLicense, interval: e.target.value })}
                    style={{ width:"100%", padding:"13px 16px", borderRadius:12, border:"1.5px solid var(--border-strong)", background:"var(--bg-elevated)", color:"var(--text)", fontSize:14 }}
                  >
                    {Object.keys(vendorPlans).length > 0 ? Object.keys(vendorPlans).map((interval) => (
                      <option key={interval} value={interval}>{formatVendorPlanLabel(interval)}</option>
                    )) : (
                      <>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="semiannual">Every 6 months</option>
                        <option value="annual">Annual</option>
                      </>
                    )}
                  </select>
                </div>
                <Button type="submit" label="Generate key" loading={vendorBusy} />
              </form>
              {generatedVendorKey?.key && (
                <div style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid var(--border)",
                  background: "var(--bg-soft)",
                  color: "var(--text)",
                  display: "grid",
                  gap: 4,
                }}>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 700, letterSpacing: 1.1, textTransform: "uppercase" }}>
                    Share this key once
                  </div>
                  <code style={{ fontSize: 18, fontWeight: 800, overflowWrap: "anywhere" }}>{generatedVendorKey.key}</code>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    Expires {formatDateTime(generatedVendorKey.expiresAt)}.
                  </span>
                </div>
              )}
            </Card>

            <div style={{ display: "grid", gap: 8 }}>
              {vendorLicenses.length === 0 ? (
                <Card>
                  <div style={{ textAlign: "center", color: "var(--text-muted)" }}>No vendor keys have been created yet.</div>
                </Card>
              ) : vendorLicenses.map((license) => (
                <article key={license.id} style={{
                  background: "var(--bg-elevated)",
                  borderRadius: 12,
                  padding: 14,
                  border: "1px solid var(--border)",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--text)", overflowWrap: "anywhere" }}>
                      {license.studio_name || license.key_label || "Vendor studio"}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {formatVendorPlanLabel(license.plan_interval)} access
                      {license.expires_at ? ` · expires ${formatDateTime(license.expires_at)}` : ""}
                      {license.redeemed_at ? ` · redeemed ${formatDateTime(license.redeemed_at)}` : ""}
                    </div>
                  </div>
                  <span style={{
                    fontSize: 11,
                    padding: "5px 10px",
                    borderRadius: 999,
                    background: license.status === "active" ? "rgba(50,160,80,0.16)" : "var(--bg-soft)",
                    color: license.status === "active" ? "#1F6E36" : "var(--text-muted)",
                    fontWeight: 800,
                    textTransform: "uppercase",
                  }}>
                    {license.status || "unknown"}
                  </span>
                </article>
              ))}
            </div>
          </>
        )}

        {tab === "promos" && (
          <>
            <Card style={{ marginBottom: 16 }}>
              <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Create promo code</h3>
              <form onSubmit={createPromo} style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 160px), 1fr))", alignItems: "flex-end" }}>
                <Input label="Code" required value={newPromo.code}
                  onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })} />
                <Input label="Description" value={newPromo.description}
                  onChange={(e) => setNewPromo({ ...newPromo, description: e.target.value })} />
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:1.4, color:"var(--accent)", marginBottom:6 }}>Type</label>
                  <select value={newPromo.discount_type}
                    onChange={(e) => setNewPromo({ ...newPromo, discount_type: e.target.value })}
                    style={{ width:"100%", padding:"13px 16px", borderRadius:12, border:"1.5px solid var(--border-strong)", background:"var(--bg-elevated)", color:"var(--text)", fontSize:14 }}>
                    <option value="percent">% off</option>
                    <option value="flat">$ off (cents)</option>
                  </select>
                </div>
                <Input label="Amount" required type="number" value={newPromo.amount}
                  onChange={(e) => setNewPromo({ ...newPromo, amount: Number(e.target.value) })} />
                <Button type="submit" label="Create" icon="+" />
              </form>
            </Card>
	            <div style={{ display: "grid", gap: 8 }}>
	              {promos.map((p) => (
	                <article key={p.id} style={{
	                  background: "var(--bg-elevated)", borderRadius: 12, padding: 14,
	                  border: "1px solid var(--border)", display: "grid", gap: 12,
	                }}>
	                  {editingPromoId === p.id && promoDraft ? (
	                    <form onSubmit={updatePromo} style={{ display: "grid", gap: 10 }}>
	                      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))", alignItems: "end" }}>
	                        <Input label="Code" required value={promoDraft.code} onChange={(e) => setPromoDraft({ ...promoDraft, code: e.target.value.toUpperCase() })} />
	                        <Input label="Description" value={promoDraft.description} onChange={(e) => setPromoDraft({ ...promoDraft, description: e.target.value })} />
	                        <div>
	                          <label style={{ display:"block", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:1.4, color:"var(--accent)", marginBottom:6 }}>Type</label>
	                          <select value={promoDraft.discount_type}
	                            onChange={(e) => setPromoDraft({ ...promoDraft, discount_type: e.target.value })}
	                            style={{ width:"100%", padding:"13px 16px", borderRadius:12, border:"1.5px solid var(--border-strong)", background:"var(--bg-elevated)", color:"var(--text)", fontSize:14 }}>
	                            <option value="percent">% off</option>
	                            <option value="flat">$ off (cents)</option>
	                          </select>
	                        </div>
	                        <Input label="Amount" required type="number" value={promoDraft.amount} onChange={(e) => setPromoDraft({ ...promoDraft, amount: Number(e.target.value) })} />
	                        <Input label="Min spend (cents)" type="number" value={promoDraft.min_amount_cents} onChange={(e) => setPromoDraft({ ...promoDraft, min_amount_cents: Number(e.target.value) })} />
	                        <Input label="Max uses" type="number" value={promoDraft.max_uses} onChange={(e) => setPromoDraft({ ...promoDraft, max_uses: e.target.value })} />
	                        <Input label="Expires" type="date" value={promoDraft.ends_at} onChange={(e) => setPromoDraft({ ...promoDraft, ends_at: e.target.value })} />
	                      </div>
	                      <label style={{ display: "flex", gap: 8, alignItems: "center", color: "var(--text)", fontSize: 13 }}>
	                        <input type="checkbox" checked={promoDraft.first_booking_only} onChange={(e) => setPromoDraft({ ...promoDraft, first_booking_only: e.target.checked })} />
	                        First booking only
	                      </label>
	                      <label style={{ display: "flex", gap: 8, alignItems: "center", color: "var(--text)", fontSize: 13 }}>
	                        <input type="checkbox" checked={promoDraft.is_active} onChange={(e) => setPromoDraft({ ...promoDraft, is_active: e.target.checked })} />
	                        Active
	                      </label>
	                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
	                        <Button type="button" size="sm" variant="outline" label="Cancel" onClick={() => { setEditingPromoId(null); setPromoDraft(null); }} />
	                        <Button type="submit" size="sm" label="Save changes" loading={promoBusy === p.id} />
	                      </div>
	                    </form>
	                  ) : (
	                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
	                      <div>
	                        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--text)" }}>
	                          {p.code}
	                        </div>
	                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
	                          {p.description || "—"} · {p.discount_type === "percent" ? `${p.amount}% off` : `${fmtCurrency(p.amount, settings?.currency_code)} off`} · used {p.uses}{p.max_uses ? `/${p.max_uses}` : ""}
	                          {p.ends_at ? ` · expires ${new Date(p.ends_at).toLocaleDateString()}` : ""}
	                        </div>
	                      </div>
	                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
	                        {p.is_active ? <span style={{ fontSize: 11, padding: "4px 10px", background: "rgba(50,160,80,0.16)", color: "#1F6E36", borderRadius: 999, fontWeight: 700 }}>ACTIVE</span>
	                                    : <span style={{ fontSize: 11, padding: "4px 10px", background: "var(--bg-soft)", color: "var(--text-muted)", borderRadius: 999, fontWeight: 700 }}>OFF</span>}
	                        <Button size="sm" variant="outline" label="Edit" onClick={() => startEditPromo(p)} />
	                        <Button size="sm" variant="outline" label={p.is_active ? "Deactivate" : "Activate"} onClick={() => togglePromo(p)} loading={promoBusy === p.id} />
	                        <Button size="sm" variant="danger" label="Delete" onClick={() => deletePromo(p)} disabled={promoBusy === p.id} />
	                      </div>
	                    </div>
	                  )}
	                </article>
	              ))}
	            </div>
          </>
        )}

        {tab === "settings" && (
          <>
            <Card style={{ marginBottom: 20, maxWidth: "100%" }}>
              <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 4 }}>
                Studio Settings
              </h3>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
                Configure your studio's location, currency, and regional preferences.
              </p>
              {settings ? (
                <StudioSettingsForm settings={settings} authedFetch={authedFetch} setSettings={setSettings} setErr={setErr} />
              ) : (
                <div style={{ textAlign: "center", padding: 24, color: "var(--text-muted)" }}>
                  Loading settings...
                </div>
              )}
            </Card>
            <Card style={{ marginBottom: 20, maxWidth: "100%" }}>
              <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 4 }}>
                💰 Pricing
              </h3>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
                Set prices for subscriptions, AI design generation, and default booking amounts. All values in your configured currency ({settings?.currency_code || "USD"}).
              </p>
              {settings ? (
                <PricingSettingsForm settings={settings} authedFetch={authedFetch} setSettings={setSettings} setErr={setErr} />
              ) : (
                <div style={{ textAlign: "center", padding: 24, color: "var(--text-muted)" }}>Loading...</div>
              )}
            </Card>
          </>
        )}

        {tab === "rotation" && (
          <>
            <SectionHeader
              emoji="🍎"
              title="Apple Secret Rotation"
              subtitle="Monitor and rotate your Apple sign-in secret."
            />

            <Card style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
                    Rotation status
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 6 }}>
                    {rotationStatus
                      ? rotationStatus.enabled
                        ? "Auto-rotation is enabled"
                        : "Auto-rotation is disabled"
                      : "Loading status..."}
                  </div>
                </div>
                <Button
                  label="Refresh status"
                  variant="outline"
                  onClick={loadRotationData}
                  disabled={rotationBusy}
                  loading={rotationBusy}
                />
              </div>
              {rotationStatus && (
                <div style={{ display: "grid", gap: 12, marginTop: 18, gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))" }}>
                  {[
                    ["Configured", rotationStatus.status.configured ? "Yes" : "No"],
                    ["Threshold", `${rotationStatus.status.thresholdDays} days`],
                    ["TTL", `${rotationStatus.status.ttlDays} days`],
                    ["Should rotate", rotationStatus.shouldRotate ? "Yes" : "No"],
                    ["Project ref", rotationStatus.status.projectRef || "Not set"],
                  ].map(([label, value]) => (
                    <div key={label} style={{ background: "var(--bg-soft)", borderRadius: 14, padding: 14, border: "1px solid var(--border)", minWidth: 140 }}>
                      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1.4, color: "var(--text-muted)", marginBottom: 6 }}>
                        {label}
                      </div>
                      <div style={{ fontSize: 15, color: "var(--text)", fontWeight: 600 }}>{value}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", margin: 0 }}>
                    Manual rotation
                  </h3>
                  <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "6px 0 0" }}>
                    Generate and deploy a new client secret immediately.
                  </p>
                </div>
                <Button
                  label="Rotate now"
                  variant="gold"
                  onClick={async () => {
                    setRotationBusy(true);
                    try {
                      await authedFetch("/api/ops/apple-rotation/rotate", { method: "POST" });
                      await loadRotationData();
                    } catch (e) {
                      setErr(e.message);
                    } finally {
                      setRotationBusy(false);
                    }
                  }}
                  loading={rotationBusy}
                />
              </div>
            </Card>

            <Card>
              <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>
                Recent rotation history
              </h3>
              {rotationHistory.length === 0 ? (
                <div style={{ color: "var(--text-muted)", padding: 16 }}>
                  No rotation events have been recorded yet.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {rotationHistory.map((event) => (
                    <article key={event.id} style={{ background: "var(--bg-elevated)", borderRadius: 12, padding: 14, border: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
                          {event.source}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          {new Date(event.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ marginTop: 10, display: "grid", gap: 6, color: "var(--text-muted)", fontSize: 13 }}>
                        <div>
                          <strong>Status:</strong> {event.status}
                        </div>
                        <div>
                          <strong>Expires:</strong> {new Date(event.expires_at).toLocaleString()}
                        </div>
                        <div>
                          <strong>Notes:</strong> {event.notes || "—"}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}

        {tab === "book" && (
          <>
            <SectionHeader emoji="📅" title="Book for Customer" subtitle="Create appointments on behalf of customers with full control." />
            <AdminBookingFlow authedFetch={authedFetch} setErr={setErr} settings={settings} />
          </>
        )}

        {tab === "gallery" && (
          <>
            <SectionHeader emoji="🖼️" title="Gallery" subtitle="Upload real nail work photos to the public gallery." />
            <AdminGalleryUpload authedFetch={authedFetch} setErr={setErr} />
          </>
        )}

        {tab === "invoices" && (
          <>
            <SectionHeader emoji="💰" title="Invoice Management" subtitle="View, create, and manage customer invoices." />
            <InvoiceManagement authedFetch={authedFetch} setErr={setErr} settings={settings} ratesData={ratesData} />
          </>
        )}

        {err && <Card style={{ marginTop: 14 }}><div role="alert" style={{ color: "#C02020" }}>{err}</div></Card>}

        {requestFor && (
          <DesignDetailModal
            req={requestFor}
            onClose={() => setRequestFor(null)}
            showCustomer
          />
        )}

        {deleteUserTarget && (
          <DeleteUserDialog
            user={deleteUserTarget}
            loading={deletingUser}
            onCancel={() => !deletingUser && setDeleteUserTarget(null)}
            onConfirm={() => deleteUser(deleteUserTarget.id)}
          />
        )}

        {/* Tech Picker Modal - Visual card-based tech assignment */}
        {showTechPicker && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              zIndex: 1000,
              backdropFilter: "blur(4px)"
            }}
            onClick={() => setShowTechPicker(null)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "var(--bg-elevated)",
                borderRadius: 20,
                padding: 28,
                maxWidth: 600,
                width: "100%",
                maxHeight: "80vh",
                overflowY: "auto",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
              }}
            >
              <div style={{ marginBottom: 20 }}>
                <h3 style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 24,
                  fontWeight: 700,
                  marginBottom: 8,
                  color: "var(--text)"
                }}>
                  Assign Technician
                </h3>
                <p style={{ fontSize: 14, color: "var(--text-muted)" }}>
                  Select a technician to assign this request
                </p>
              </div>

              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 160px), 1fr))",
                gap: 12
              }}>
                {techs.map(t => {
                    const techShare = Number(t.technician?.commission_pct ?? 80);
                    const studioShare = Math.max(0, 100 - Math.min(Math.max(techShare, 0), 100));
                    return (
                      <button
                        key={t.id}
                        onClick={async () => {
                          await assign(showTechPicker, t.id);
                          setShowTechPicker(null);
                        }}
                        style={{
                          padding: 16,
                          background: "var(--bg-soft)",
                          border: "1px solid var(--border)",
                          borderRadius: 12,
                          cursor: "pointer",
                          textAlign: "center",
                          transition: "all 0.2s ease",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "var(--mauve)";
                          e.currentTarget.style.background = "rgba(196,135,154,0.08)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 8px 22px rgba(196,135,154,0.18)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = "var(--border)";
                          e.currentTarget.style.background = "var(--bg-soft)";
                          e.currentTarget.style.transform = "";
                          e.currentTarget.style.boxShadow = "";
                        }}
                      >
                        <div style={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 22,
                          marginBottom: 12
                        }}>
                          {(t.full_name || t.email)[0].toUpperCase()}
                        </div>
                        <div style={{
                          fontWeight: 600,
                          fontSize: 14,
                          marginBottom: 4,
                          color: "var(--text)"
                        }}>
                          {t.full_name || "Tech"}
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: "var(--text-muted)",
                          wordBreak: "break-all"
                        }}>
                          {t.email}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>
                          Tech {techShare}% · Studio {studioShare}%
                        </div>
                      </button>
                    );
                  })}
              </div>

              <div style={{ marginTop: 20, textAlign: "center" }}>
                <button
                  onClick={() => setShowTechPicker(null)}
                  style={{
                    padding: "10px 24px",
                    borderRadius: 999,
                    border: "1.5px solid var(--border)",
                    background: "var(--bg)",
                    color: "var(--text)",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.15s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--border-strong)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "var(--border)";
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DeleteUserDialog({ user, loading, onCancel, onConfirm }) {
  const [confirmText, setConfirmText] = useState("");
  const displayName = user.full_name || user.email || "this user";
  const canDelete = confirmText === "DELETE";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-user-title"
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgba(13, 8, 16, 0.62)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 500,
          background: "var(--bg-elevated)",
          color: "var(--text)",
          border: "1px solid var(--border-strong)",
          borderRadius: 20,
          boxShadow: "0 28px 80px rgba(13, 8, 16, 0.38)",
          overflow: "hidden",
        }}
      >
        <div style={{
          padding: 24,
          background: "linear-gradient(135deg, rgba(200,30,30,0.12), rgba(196,135,154,0.06))",
          borderBottom: "1px solid var(--border)",
        }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "rgba(200,30,30,0.12)",
            color: "#C81E1E",
            display: "grid",
            placeItems: "center",
            fontSize: 22,
            marginBottom: 14,
          }}>
            !
          </div>
          <h3 id="delete-user-title" style={{
            fontFamily: "var(--font-display)",
            fontSize: 24,
            margin: 0,
            color: "var(--text)",
          }}>
            Delete {displayName}?
          </h3>
          <p style={{ margin: "8px 0 0", color: "var(--text-muted)", fontSize: 14, lineHeight: 1.55 }}>
            This permanently removes their login account and related profile access. This cannot be undone.
          </p>
        </div>

        <div style={{ padding: 24, display: "grid", gap: 16 }}>
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.8, color: "var(--text-muted)" }}>
              TYPE DELETE TO CONFIRM
            </span>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoFocus
              disabled={loading}
              placeholder="DELETE"
              style={{
                width: "100%",
                boxSizing: "border-box",
                borderRadius: 12,
                border: "1px solid var(--border-strong)",
                background: "var(--bg-soft)",
                color: "var(--text)",
                padding: "13px 14px",
                fontSize: 15,
                fontWeight: 700,
              }}
            />
          </label>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <Button label="Cancel" variant="outline" onClick={onCancel} disabled={loading} />
            <Button
              label="Delete forever"
              variant="danger"
              onClick={onConfirm}
              disabled={!canDelete || loading}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminBookingsBoard({ bookings, techs, onAssign, authedFetch, onRefresh, emptyText = "No bookings yet." }) {
  const [confirming, setConfirming] = useState(null);
  const [verifying, setVerifying] = useState(null);

  async function confirmPayment(bookingId) {
    setConfirming(bookingId);
    try {
      await authedFetch(`/api/invoices/booking/${bookingId}/mark-paid`, { method: "POST" });
      await onRefresh?.();
    } catch (e) {
      window.alert(e.message || "Could not mark invoice as paid.");
    }
    setConfirming(null);
  }

  async function verifyDeposit(bookingId) {
    if (!window.confirm("Confirm you've received this customer's deposit by transfer/cash? This will confirm the booking.")) return;
    setVerifying(bookingId);
    try {
      await authedFetch(`/api/bookings/${bookingId}/deposit/verify`, { method: "POST" });
      await onRefresh?.();
    } catch (e) {
      window.alert(e.message || "Could not verify the deposit.");
    }
    setVerifying(null);
  }
  const groups = [
    ["Awaiting deposit verification", bookings.filter((b) => b.status === "payment_pending")],
    ["Awaiting customer confirmation", bookings.filter((b) => b.status === "pending_customer_confirmation")],
    ["Upcoming", bookings.filter((b) => ["pending", "confirmed"].includes(b.status))],
    ["In progress", bookings.filter((b) => b.status === "in_progress")],
    ["Rescheduled", bookings.filter((b) => b.status === "rescheduled")],
    ["No Show", bookings.filter((b) => ["no_show", "no-show"].includes(b.status))],
    ["Cancelled", bookings.filter((b) => ["cancelled", "canceled"].includes(b.status))],
    ["Completed", bookings.filter((b) => ["completed", "complete"].includes(b.status))],
  ];
  const visible = groups.filter(([, rows]) => rows.length > 0);
  if (!bookings.length) {
    return <Card><div style={{ textAlign: "center", color: "var(--text-muted)" }}>{emptyText}</div></Card>;
  }
  return (
    <div style={{ display: "grid", gap: 18 }}>
      {visible.map(([title, rows]) => (
        <section key={title} style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", margin: 0, fontSize: 22 }}>
              {title}
            </h3>
            <span style={{
              borderRadius: 999,
              padding: "4px 10px",
              background: "rgba(201,168,76,0.16)",
              color: "var(--text)",
              fontSize: 12,
              fontWeight: 800,
            }}>{rows.length}</span>
          </div>
          {rows.map((b) => (
            <article key={b.id} style={{
              background: "var(--bg-elevated)",
              borderRadius: 14,
              padding: 16,
              border: "1px solid var(--border)",
              display: "grid",
              gap: 12,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, color: "var(--text)", fontSize: 15 }}>
                    {b.customer?.full_name || b.customer?.email || "Customer"}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-accent)", marginTop: 3, fontWeight: 800, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                    Booking #{bookingDisplayCode(b.id)}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>
                    {b.request?.final_design?.name || "(no final pick)"} · Appointment: {b.slot ? new Date(b.slot.starts_at).toLocaleString() : "No slot"}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>
                    Booked: {b.created_at ? new Date(b.created_at).toLocaleString() : "Unknown"}
                    {["completed", "complete"].includes(b.status) && b.updated_at ? ` · Completed: ${new Date(b.updated_at).toLocaleString()}` : ""}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>
                    Technician: <strong style={{ color: "var(--text)" }}>{b.technician?.full_name || b.technician?.email || "Unassigned"}</strong>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <span style={{
                    fontSize: 11, fontWeight: 800, padding: "5px 12px", borderRadius: 999,
                    background: statusBadgeBg(b.status), color: statusBadgeFg(b.status)
                  }}>{formatStatus(b.status)}</span>
                  {b.booking_source === "admin" && (
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: "5px 10px", borderRadius: 999,
                      background: "rgba(201,168,76,0.18)", color: "var(--text-gold)",
                      letterSpacing: 0.8
                    }}>ADMIN BOOKING</span>
                  )}
                  {b.status === "payment_pending" && (
                    <button
                      onClick={() => verifyDeposit(b.id)}
                      disabled={verifying === b.id}
                      style={{
                        fontSize: 11, fontWeight: 800, padding: "5px 12px", borderRadius: 999, cursor: "pointer",
                        background: "#1F6E36", color: "#fff", border: "none",
                        opacity: verifying === b.id ? 0.6 : 1,
                      }}
                    >
                      {verifying === b.id ? "Verifying…" : "✓ Verify deposit & confirm"}
                    </button>
                  )}
                  {b.invoice?.customer_declared_at && b.invoice?.status !== "paid" && (
                    <button
                      onClick={() => confirmPayment(b.id)}
                      disabled={confirming === b.id}
                      style={{
                        fontSize: 11, fontWeight: 800, padding: "5px 12px", borderRadius: 999, cursor: "pointer",
                        background: "var(--accent)", color: "#fff", border: "none",
                        opacity: confirming === b.id ? 0.6 : 1,
                      }}
                    >
                      {confirming === b.id ? "Confirming…" : "✓ Confirm Payment"}
                    </button>
                  )}
                  {b.invoice?.customer_declared_at && b.invoice?.status !== "paid" && (
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: "5px 10px", borderRadius: 999,
                      background: "rgba(196,135,154,0.18)", color: "var(--accent)",
                      letterSpacing: 0.8
                    }}>PAYMENT DECLARED</span>
                  )}
                  <select
                    value={b.technician_id || ""}
                    onChange={(e) => e.target.value && onAssign(b.id, e.target.value)}
                    style={{
                      borderRadius: 999,
                      border: "1px solid var(--border-strong)",
                      background: b.technician_id ? "rgba(196,135,154,0.08)" : "var(--bg)",
                      color: "var(--text)",
                      padding: "7px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    <option value="">{b.technician_id ? "Reassign technician" : "Assign technician"}</option>
                    {techs.map((t) => (
                      <option key={t.id} value={t.id}>{t.full_name || t.email}</option>
                    ))}
                  </select>
                </div>
              </div>
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}

/**
 * AdminBookingFlow - Create bookings on behalf of customers
 * Flow: 1 → Select customer · 2 → Select design · 3 → Pick tech then slot · 4 → Confirm
 */
function AdminBookingFlow({ authedFetch, setErr, settings }) {
  const defaultBookingPriceCents = Number(settings?.default_booking_price_cents || 0);
  const [step, setStep] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  // Step 3: tech first, then slots for that tech
  const [technicians, setTechnicians] = useState([]);
  const [selectedTech, setSelectedTech] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [priceCents, setPriceCents] = useState(defaultBookingPriceCents);
  const [bookingNotes, setBookingNotes] = useState("");
  const [requireConfirmation, setRequireConfirmation] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPriceCents(defaultBookingPriceCents);
  }, [defaultBookingPriceCents]);

  // Load customers on mount
  useEffect(() => { loadCustomers(); }, []);

  async function loadCustomers() {
    try {
      const out = await authedFetch("/api/admin/users");
      setCustomers((out.users || []).filter(p => p.role === "customer" || p.role === "admin_tech" ? false : true).filter(p => p.role === "customer"));
    } catch (e) {
      setErr(e.message);
    }
  }

  async function loadDesignsForCustomer(customerId) {
    try {
      const out = await authedFetch(`/api/designs/history?customerId=${customerId}`).catch(() => null);
      // Try to get requests (they contain generated designs)
      const reqOut = await authedFetch(`/api/admin/requests`).catch(() => ({ requests: [] }));
      const customerRequests = (reqOut.requests || []).filter(r => r.customer_id === customerId);
      setDesigns(customerRequests.flatMap(r => r.generated || []));
    } catch (e) {
      setErr(e.message);
    }
  }

  async function loadTechnicians() {
    try {
      const out = await authedFetch("/api/admin/technicians");
      setTechnicians((out.technicians || []).filter(t => t.technician?.is_active !== false));
    } catch (e) {
      setErr(e.message);
    }
  }

  async function loadSlotsForTech(techId) {
    setSlotsLoading(true);
    setSlots([]);
    setSelectedSlot(null);
    try {
      const from = new Date().toISOString();
      const to = new Date(Date.now() + 30 * 86400_000).toISOString();
      const out = await authedFetch(`/api/availability?technicianId=${techId}&from=${from}&to=${to}`);
      setSlots(out.slots || []);
    } catch (e) {
      setErr(e.message);
    }
    setSlotsLoading(false);
  }

  function selectCustomer(customer) {
    setSelectedCustomer(customer);
    loadDesignsForCustomer(customer.id);
    setStep(2);
  }

  function selectDesign(design) {
    setSelectedDesign(design);
    loadTechnicians();
    setStep(3);
  }

  function selectTech(tech) {
    setSelectedTech(tech);
    loadSlotsForTech(tech.id);
  }

  function selectSlot(slot) {
    setSelectedSlot(slot);
  }

  async function confirmBooking() {
    if (!selectedCustomer || !selectedSlot) return;
    if (!selectedTech) { setErr("Please select a technician before confirming."); return; }
    setLoading(true);
    try {
      const body = {
        customerId: selectedCustomer.id,
        slotId: selectedSlot.id,
        technicianId: selectedTech.id,
        category: selectedDesign?.category || "walk-in",
        priceCents,
        notes: bookingNotes || "",
        requireConfirmation,
      };
      // If a specific design was chosen, link it to a request if possible
      if (selectedDesign?.requestId) {
        body.requestId = selectedDesign.requestId;
      }
      await authedFetch("/api/admin/bookings", { method: "POST", body: JSON.stringify(body) });

      // If cash payment, admin can create an invoice separately
      if (paymentMethod === "cash") {
        // Invoice creation is handled via the Invoices tab
      }

      // Reset
      setStep(1);
      setSelectedCustomer(null);
      setSelectedDesign(null);
      setSelectedSlot(null);
      setSelectedTech(null);
      setSlots([]);
      setBookingNotes("");
      setPriceCents(defaultBookingPriceCents);
      setRequireConfirmation(true);
      setErr("");
      alert(requireConfirmation
        ? "✅ Booking sent to the customer to confirm. They'll pay the deposit to lock it in."
        : "✅ Booking created and confirmed for customer!");
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }

  const filteredCustomers = customers.filter(c =>
    !searchTerm ||
    c.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Progress Steps */}
      <Card>
        <div style={{ display: "flex", gap: "clamp(8px, 2vw, 16px)", alignItems: "center", flexWrap: "wrap" }}>
          {[
            { num: 1, label: "Customer", icon: "👤" },
            { num: 2, label: "Design", icon: "💅" },
            { num: 3, label: "Slot & Tech", icon: "📅" },
            { num: 4, label: "Confirm", icon: "✅" }
          ].map(({ num, label, icon }) => (
            <div key={num} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: step >= num ? "linear-gradient(135deg, var(--mauve), var(--mauve-deep))" : "var(--bg-soft)",
                color: step >= num ? "#fff" : "var(--text-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 16
              }}>
                {step > num ? "✓" : icon}
              </div>
              <div style={{ fontSize: 13, fontWeight: step === num ? 700 : 400, color: step >= num ? "var(--text)" : "var(--text-muted)" }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Step 1: Select Customer */}
      {step === 1 && (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>Search Customer</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 8,
                border: "1.5px solid var(--border-strong)",
                background: "var(--bg)",
                color: "var(--text)",
                fontSize: 14
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 400, overflowY: "auto" }}>
            {filteredCustomers.map(c => (
              <button
                key={c.id}
                onClick={() => selectCustomer(c)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: 12,
                  background: "var(--bg-soft)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.15s ease"
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(196,135,154,0.08)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "var(--bg-soft)"}
              >
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 16
                }}>
                  {(c.full_name || c.email)[0].toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{c.full_name || "No name"}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{c.email}</div>
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Step 2: Select Design */}
      {step === 2 && (
        <>
          <Card>
            <div style={{ marginBottom: 12 }}>
              <strong>Customer:</strong> {selectedCustomer?.full_name || selectedCustomer?.email}
            </div>
            <Button variant="outline" label="← Change Customer" onClick={() => setStep(1)} />
          </Card>
          <Card>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Select Design</div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
              gap: 12
            }}>
              {designs.length === 0 && (
                <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)", gridColumn: "1 / -1" }}>
                  No designs found for this customer. You can generate new designs in the booking flow.
                </div>
              )}
              {designs.map(d => (
                <button
                  key={d.id}
                  onClick={() => selectDesign(d)}
                  style={{
                    padding: 12,
                    background: "var(--bg-soft)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.15s ease"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--mauve)"}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--border)"}
                >
                  {d.image_url && (
                    <img src={d.image_url} alt={d.name} style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 6, marginBottom: 8 }} />
                  )}
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{d.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{d.category}</div>
                </button>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Step 3: Pick Technician first, then see their available slots */}
      {step === 3 && (
        <>
          <Card>
            <div style={{ marginBottom: 12 }}>
              <strong>Design:</strong> {selectedDesign?.name || "(Walk-in — no design yet)"}
            </div>
            <Button variant="outline" label="← Change Design" onClick={() => setStep(2)} />
          </Card>

          {/* Tech picker */}
          <Card>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>1. Select Technician</div>
            <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>
              Choose a technician — their available slots will appear below.
            </p>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 160px), 1fr))",
              gap: 12
            }}>
              {technicians.map(t => (
                <button
                  key={t.id}
                  onClick={() => selectTech(t)}
                  style={{
                    padding: 14,
                    background: selectedTech?.id === t.id ? "rgba(196,135,154,0.12)" : "var(--bg-soft)",
                    border: selectedTech?.id === t.id ? "2px solid var(--mauve)" : "1px solid var(--border)",
                    borderRadius: 12,
                    cursor: "pointer",
                    textAlign: "center",
                    transition: "all 0.15s ease"
                  }}
                >
                  <div style={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    background: selectedTech?.id === t.id
                      ? "linear-gradient(135deg, var(--mauve), var(--mauve-deep))"
                      : "var(--bg-elevated)",
                    color: selectedTech?.id === t.id ? "#fff" : "var(--text)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 20,
                    margin: "0 auto 10px",
                    border: "2px solid var(--border)"
                  }}>
                    {t.avatar_url
                      ? <img src={t.avatar_url} alt="" style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover" }} />
                      : (t.full_name || t.email)[0].toUpperCase()}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text)" }}>{t.full_name || "Tech"}</div>
                  {selectedTech?.id === t.id && (
                    <div style={{ fontSize: 10, color: "var(--mauve)", fontWeight: 700, marginTop: 4 }}>✓ Selected</div>
                  )}
                </button>
              ))}
              {technicians.length === 0 && (
                <div style={{ color: "var(--text-muted)", fontSize: 13, gridColumn: "1 / -1" }}>No active technicians found.</div>
              )}
            </div>
          </Card>

          {/* Slot picker — only shows after a tech is selected */}
          {selectedTech && (
            <Card>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                2. Select Slot — {selectedTech.full_name || selectedTech.email}
              </div>
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14 }}>
                All open slots for the next 30 days.
              </p>
              {slotsLoading ? (
                <div style={{ textAlign: "center", padding: 24, color: "var(--text-muted)" }}>Loading slots…</div>
              ) : slots.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: "var(--text-muted)" }}>
                  No open slots found for this technician in the next 30 days.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
                  {slots.map(s => (
                    <button
                      key={s.id}
                      onClick={() => selectSlot(s)}
                      style={{
                        padding: "12px 14px",
                        background: selectedSlot?.id === s.id ? "rgba(196,135,154,0.12)" : "var(--bg-soft)",
                        border: selectedSlot?.id === s.id ? "2px solid var(--mauve)" : "1px solid var(--border)",
                        borderRadius: 10,
                        cursor: "pointer",
                        textAlign: "left",
                        transition: "all 0.15s ease",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>
                          {new Date(s.starts_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          {new Date(s.starts_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                          {" → "}
                          {new Date(s.ends_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                        </div>
                      </div>
                      {selectedSlot?.id === s.id && (
                        <span style={{ fontSize: 11, color: "var(--mauve)", fontWeight: 700 }}>✓ Selected</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {selectedSlot && (
                <div style={{ marginTop: 16, textAlign: "right" }}>
                  <Button
                    label="Continue to Confirm →"
                    variant="primary"
                    onClick={() => setStep(4)}
                  />
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Step 4: Confirm & Payment */}
      {step === 4 && (
        <>
          <Card>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Booking Summary</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <SummaryRow label="Customer" value={selectedCustomer?.full_name || selectedCustomer?.email} />
              <SummaryRow label="Technician" value={selectedTech?.full_name || selectedTech?.email || "—"} />
              <SummaryRow label="Design" value={selectedDesign?.name || "(Walk-in)"} />
              <SummaryRow
                label="Date & Time"
                value={selectedSlot
                  ? `${new Date(selectedSlot.starts_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} at ${new Date(selectedSlot.starts_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
                  : "—"}
              />
            </div>
          </Card>
          <Card>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Price</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14 }}>{CURRENCY_SYMBOLS[settings?.currency_code] || '$'}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={(priceCents / 100).toFixed(2)}
                onChange={(e) => setPriceCents(Math.round(parseFloat(e.target.value || 0) * 100))}
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: "1.5px solid var(--border-strong)",
                  background: "var(--bg)",
                  color: "var(--text)",
                  fontSize: 16,
                  fontWeight: 700,
                  width: 120
                }}
              />
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Booking price (can be updated later)</span>
            </div>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 14, cursor: "pointer" }}>
              <input type="checkbox" checked={requireConfirmation} onChange={(e) => setRequireConfirmation(e.target.checked)} style={{ marginTop: 3 }} />
              <span style={{ fontSize: 13, color: "var(--text)" }}>
                <strong>Let the customer confirm &amp; pay deposit</strong>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                  The slot is held and the customer reviews the price, confirms, and pays the deposit to lock it in. Uncheck to confirm a walk-in on the spot.
                </div>
              </span>
            </label>
          </Card>
          <Card>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Payment Method</div>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => setPaymentMethod("cash")}
                style={{
                  flex: 1, padding: 16,
                  background: paymentMethod === "cash" ? "rgba(196,135,154,0.12)" : "var(--bg-soft)",
                  border: paymentMethod === "cash" ? "2px solid var(--mauve)" : "1px solid var(--border)",
                  borderRadius: 8, cursor: "pointer", fontWeight: 600
                }}
              >
                💵 Cash / In-Person
              </button>
              <button
                onClick={() => setPaymentMethod("stripe")}
                style={{
                  flex: 1, padding: 16,
                  background: paymentMethod === "stripe" ? "rgba(196,135,154,0.12)" : "var(--bg-soft)",
                  border: paymentMethod === "stripe" ? "2px solid var(--mauve)" : "1px solid var(--border)",
                  borderRadius: 8, cursor: "pointer", fontWeight: 600
                }}
              >
                💳 Card (Stripe)
              </button>
            </div>
            {paymentMethod === "cash" && (
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 10 }}>
                Booking will be confirmed immediately. You can create a cash invoice from the Invoices tab.
              </p>
            )}
          </Card>
          <Card>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>Notes (optional)</div>
            <textarea
              value={bookingNotes}
              onChange={(e) => setBookingNotes(e.target.value)}
              placeholder="Any special instructions for this booking…"
              rows={3}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: 8,
                border: "1.5px solid var(--border-strong)",
                background: "var(--bg)",
                color: "var(--text)",
                fontSize: 14,
                fontFamily: "inherit",
                resize: "vertical"
              }}
            />
          </Card>
          <div style={{ display: "flex", gap: 12 }}>
            <Button variant="outline" label="← Back" onClick={() => setStep(3)} />
            <Button
              label={loading ? "Creating…" : "Confirm Booking for Customer"}
              onClick={confirmBooking}
              disabled={loading || !selectedTech || !selectedSlot}
              style={{ flex: 1 }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
      <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{value}</span>
    </div>
  );
}

/**
 * ManualInvoiceForm - Create invoices independent of bookings
 */
function ManualInvoiceForm({ authedFetch, setErr, settings, onClose, onSuccess }) {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [items, setItems] = useState([{ description: "", quantity: 1, unitCents: 0 }]);
  const [taxCents, setTaxCents] = useState(0);
  const [discountCents, setDiscountCents] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCustomers();
  }, []);

  async function loadCustomers() {
    try {
      const out = await authedFetch("/api/admin/users");
      setCustomers((out.users || []).filter(p => p.role === "customer"));
    } catch (e) {
      setErr(e.message);
    }
  }

  function addItem() {
    setItems([...items, { description: "", quantity: 1, unitCents: 0 }]);
  }

  function removeItem(index) {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  }

  function updateItem(index, field, value) {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  }

  const subtotalCents = items.reduce((sum, item) => {
    return sum + (Number(item.quantity) || 0) * (Number(item.unitCents) || 0);
  }, 0);

  const totalCents = Math.max(0, subtotalCents + Number(taxCents) - Number(discountCents));

  async function createInvoice() {
    if (!selectedCustomer) {
      setErr("Please select a customer");
      return;
    }
    if (items.length === 0 || items.every(item => !item.description)) {
      setErr("Please add at least one line item");
      return;
    }

    setLoading(true);
    try {
      await authedFetch("/api/invoices", {
        method: "POST",
        body: JSON.stringify({
          customerId: selectedCustomer.id,
          items: items.map(item => ({
            description: item.description,
            quantity: Number(item.quantity) || 1,
            unitCents: Number(item.unitCents) || 0
          })),
          method: paymentMethod,
          currency: settings?.currency_code || "USD",
          notes,
          taxCents: Number(taxCents) || 0,
          discountCents: Number(discountCents) || 0
        })
      });
      onSuccess();
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }

  const filteredCustomers = customers.filter(c =>
    c.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontFamily: "var(--font-display)", fontSize: 20, marginBottom: 8, color: "var(--text)" }}>
          Create Manual Invoice
        </h3>
        <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Generate invoices for services not tied to bookings
        </p>
      </div>

      {/* Step 1: Customer Selection */}
      {!selectedCustomer ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
              Search Customer
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 8,
                border: "1.5px solid var(--border-strong)",
                background: "var(--bg)",
                color: "var(--text)",
                fontSize: 14
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 300, overflowY: "auto" }}>
            {filteredCustomers.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedCustomer(c)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: 12,
                  background: "var(--bg-soft)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.15s ease"
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(196,135,154,0.08)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "var(--bg-soft)"}
              >
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 16
                }}>
                  {(c.full_name || c.email)[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text)" }}>
                    {c.full_name || "No name"}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {c.email}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Selected Customer Display */}
          <div style={{
            padding: 16,
            background: "var(--bg-soft)",
            border: "1.5px solid var(--border)",
            borderRadius: 8,
            marginBottom: 20,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 16
              }}>
                {(selectedCustomer.full_name || selectedCustomer.email)[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text)" }}>
                  {selectedCustomer.full_name || "No name"}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {selectedCustomer.email}
                </div>
              </div>
            </div>
            <button
              onClick={() => setSelectedCustomer(null)}
              style={{
                padding: "6px 12px",
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                fontSize: 12,
                cursor: "pointer",
                color: "var(--text)"
              }}
            >
              Change
            </button>
          </div>

          {/* Line Items */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <label style={{ fontWeight: 600, fontSize: 14 }}>Line Items</label>
              <Button
                variant="outline"
                label="+ Add Item"
                onClick={addItem}
                style={{ fontSize: 12, padding: "6px 12px" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {items.map((item, index) => (
                <div key={index} style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))",
                  gap: 8,
                  alignItems: "center",
                  padding: 12,
                  background: "var(--bg-soft)",
                  border: "1px solid var(--border)",
                  borderRadius: 8
                }}>
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateItem(index, "description", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--border)",
                      background: "var(--bg)",
                      color: "var(--text)",
                      fontSize: 13
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Qty"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, "quantity", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--border)",
                      background: "var(--bg)",
                      color: "var(--text)",
                      fontSize: 13
                    }}
                  />
                  <input
                    type="number"
                    placeholder={`Price (${settings?.currency_code || "USD"})`}
                    min="0"
                    step="0.01"
                    value={(item.unitCents / 100).toFixed(2)}
                    onChange={(e) => updateItem(index, "unitCents", Math.round(parseFloat(e.target.value || 0) * 100))}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--border)",
                      background: "var(--bg)",
                      color: "var(--text)",
                      fontSize: 13
                    }}
                  />
                  {items.length > 1 && (
                    <button
                      onClick={() => removeItem(index)}
                      style={{
                        padding: "8px 12px",
                        background: "rgba(220,38,38,0.1)",
                        border: "1px solid rgba(220,38,38,0.3)",
                        borderRadius: 6,
                        cursor: "pointer",
                        color: "#DC2626",
                        fontSize: 12,
                        fontWeight: 600
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Tax and Discount */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))",
            gap: 12,
            marginBottom: 20
          }}>
            <div>
              <label style={{ display: "block", fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                Tax ({settings?.currency_code || "USD"})
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={(taxCents / 100).toFixed(2)}
                onChange={(e) => setTaxCents(Math.round(parseFloat(e.target.value || 0) * 100))}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 6,
                  border: "1.5px solid var(--border-strong)",
                  background: "var(--bg)",
                  color: "var(--text)",
                  fontSize: 13
                }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                Discount ({settings?.currency_code || "USD"})
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={(discountCents / 100).toFixed(2)}
                onChange={(e) => setDiscountCents(Math.round(parseFloat(e.target.value || 0) * 100))}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 6,
                  border: "1.5px solid var(--border-strong)",
                  background: "var(--bg)",
                  color: "var(--text)",
                  fontSize: 13
                }}
              />
            </div>
          </div>

          {/* Payment Method */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
              Payment Method
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 6,
                border: "1.5px solid var(--border-strong)",
                background: "var(--bg)",
                color: "var(--text)",
                fontSize: 13,
                cursor: "pointer"
              }}
            >
              <option value="cash">Cash</option>
              <option value="transfer">Bank Transfer</option>
              <option value="pos">POS/Card Terminal</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes..."
              rows={3}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 6,
                border: "1.5px solid var(--border-strong)",
                background: "var(--bg)",
                color: "var(--text)",
                fontSize: 13,
                fontFamily: "inherit",
                resize: "vertical"
              }}
            />
          </div>

          {/* Totals Display */}
          <div style={{
            padding: 16,
            background: "var(--bg-soft)",
            border: "1.5px solid var(--border)",
            borderRadius: 8,
            marginBottom: 20
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 13 }}>
              <span style={{ color: "var(--text-muted)" }}>Subtotal:</span>
              <span style={{ fontWeight: 600 }}>{fmtCurrency(subtotalCents, settings?.currency_code)}</span>
            </div>
            {taxCents > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 13 }}>
                <span style={{ color: "var(--text-muted)" }}>Tax:</span>
                <span style={{ fontWeight: 600 }}>+{fmtCurrency(taxCents, settings?.currency_code)}</span>
              </div>
            )}
            {discountCents > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 13 }}>
                <span style={{ color: "var(--text-muted)" }}>Discount:</span>
                <span style={{ fontWeight: 600, color: "#16A34A" }}>-{fmtCurrency(discountCents, settings?.currency_code)}</span>
              </div>
            )}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              paddingTop: 12,
              borderTop: "1px solid var(--border)",
              fontSize: 16,
              fontWeight: 700
            }}>
              <span>Total:</span>
              <span>{fmtCurrency(totalCents, settings?.currency_code)}</span>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <button
              onClick={onClose}
              disabled={loading}
              style={{
                padding: "10px 20px",
                borderRadius: 8,
                border: "1.5px solid var(--border)",
                background: "var(--bg)",
                color: "var(--text)",
                fontSize: 14,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            <Button
              variant="primary"
              label={loading ? "Creating..." : "Create Invoice"}
              onClick={createInvoice}
              disabled={loading}
              style={{ fontWeight: 600 }}
            />
          </div>
        </>
      )}
    </Card>
  );
}

function formatStatus(raw = "") {
  const value = String(raw).toLowerCase();
  if (value === "in_progress") return "In Progress";
  if (value === "no_show" || value === "no-show") return "No Show";
  if (value === "pending_customer_confirmation") return "Awaiting Confirmation";
  if (value === "payment_pending") return "Deposit — Awaiting Verification";
  return String(raw)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusBadgeBg(status = "") {
  const value = String(status).toLowerCase();
  if (value === "paid" || value === "confirmed" || value === "completed" || value === "complete") return "rgba(50,160,80,0.16)";
  if (value === "pending" || value === "rescheduled") return "rgba(201,168,76,0.16)";
  if (value === "in_progress") return "rgba(196,135,154,0.18)";
  if (value === "cancelled" || value === "canceled" || value === "void" || value === "no_show" || value === "no-show") return "rgba(200,30,30,0.12)";
  return "rgba(196,135,154,0.16)";
}

function statusBadgeFg(status = "") {
  const value = String(status).toLowerCase();
  if (value === "paid" || value === "confirmed" || value === "completed" || value === "complete") return "#1F6E36";
  if (value === "pending" || value === "rescheduled") return "var(--gold-dark, #8A6500)";
  if (value === "cancelled" || value === "canceled" || value === "void" || value === "no_show" || value === "no-show") return "#C02020";
  return "var(--text)";
}

function invoiceItems(invoice) {
  return invoice.items || invoice.line_items || [];
}

async function shareText(title, text) {
  if (navigator.share) {
    await navigator.share({ title, text });
    return;
  }
  await navigator.clipboard?.writeText(text);
}

function shareInvoice(invoice, currencyCode, ratesData = null) {
  const targetCurrency = currencyCode || invoice.currency || "USD";
  const fmt = (c) => fmtCurrency(
    convertCurrencyCents(c, invoice.currency || targetCurrency, targetCurrency, ratesData),
    targetCurrency
  );
  const lines = [
    `Oge Studio Invoice ${invoice.number || invoice.id?.slice(0, 8) || ""}`,
    `Status: ${formatStatus(invoice.status)}`,
    `Customer: ${invoice.customer?.full_name || invoice.customer?.email || "Customer"}`,
    `Total: ${fmt(invoice.total_cents || 0)}`,
    `Payment method: ${invoice.method || "cash"}`,
    "",
    ...invoiceItems(invoice).map((item) => `- ${item.description}: ${fmt(item.amount_cents || 0)}`)
  ].join("\n");
  return shareText("Oge Studio Invoice", lines).catch(() => {});
}

function shareReport(overview, byTech = [], currencyCode) {
  const fmt  = (c) => fmtCurrency(c, currencyCode);
  const conv = overview.conversionMeta;
  const usdLine = conv && conv.target !== "USD" && overview.revenueUsdCents != null
    ? `Revenue (USD ≈): ${fmtCurrency(overview.revenueUsdCents, "USD")}`
    : null;
  const rateLine = conv && conv.target !== "USD"
    ? `Rate used: 1 USD = ${conv.rateUsdPerTarget?.toFixed(4)} ${conv.target} (${conv.rateDate} · ${conv.source?.split("(")[0]?.trim()})`
    : null;

  const lines = [
    "Oge Studio Report - Last 30 days",
    `Revenue: ${fmt(overview.revenueCents || 0)}`,
    usdLine,
    rateLine,
    `Bookings: ${overview.bookingCount || 0}`,
    `Completed: ${overview.completedCount || 0}`,
    `Cancelled: ${overview.cancelledCount || 0}`,
    `No-shows: ${overview.noShowCount || 0}`,
    `Customers: ${overview.uniqueCustomers || 0}`,
    `Repeat customers: ${overview.repeatCustomers || 0}`,
    "",
    "By technician:",
    ...byTech.map((t) => {
      const usd = t.grossUsd != null && currencyCode !== "USD" ? ` ≈ ${fmtCurrency(t.grossUsd, "USD")}` : "";
      return `${t.name || t.technician_id}: ${fmtCurrency(t.gross || 0)}${usd} · ${t.count} bookings · ${t.noShows} no-shows`;
    }),
  ].filter((l) => l != null).join("\n");
  return shareText("Oge Studio Report", lines).catch(() => {});
}

function downloadReportCsv(overview, byTech = []) {
  const conv     = overview.conversionMeta;
  const showUsd  = conv && conv.target !== "USD";
  const currency = overview.currency || "USD";

  const metaRows = showUsd ? [
    [`exchange_rate_used (1 USD = X ${conv.target})`, conv.rateUsdPerTarget?.toFixed(8) ?? ""],
    ["rate_date", conv.rateDate ?? ""],
    ["rate_source", conv.source?.split("(")[0]?.trim() ?? ""],
    [],
  ] : [];

  const rows = [
    ["metric", `value_${currency}`, showUsd ? "value_USD" : null].filter(Boolean),
    ["revenue",          ((overview.revenueCents    || 0) / 100).toFixed(2), showUsd ? ((overview.revenueUsdCents   || 0) / 100).toFixed(2) : null],
    ["bookings",          overview.bookingCount    || 0, showUsd ? "" : null],
    ["completed",         overview.completedCount  || 0, showUsd ? "" : null],
    ["cancelled",         overview.cancelledCount  || 0, showUsd ? "" : null],
    ["no_shows",          overview.noShowCount     || 0, showUsd ? "" : null],
    ["customers",         overview.uniqueCustomers || 0, showUsd ? "" : null],
    ["repeat_customers",  overview.repeatCustomers || 0, showUsd ? "" : null],
    [],
    ...metaRows,
    ["technician", `gross_${currency}`, showUsd ? "gross_USD" : null, "bookings", "no_shows"].filter(Boolean),
    ...byTech.map((t) => [
      t.name || t.technician_id,
      ((t.gross || 0) / 100).toFixed(2),
      ...(showUsd ? [((t.grossUsd || 0) / 100).toFixed(2)] : []),
      t.count,
      t.noShows,
    ]),
  ].map((row) => row.filter((c) => c != null));

  const csv = rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `oge-studio-report-${currency}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * InvoiceManagement - View and manage invoices
 */
function InvoiceManagement({ authedFetch, setErr, settings, ratesData }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const displayCurrency = settings?.currency_code || "USD";
  const invoiceMoney = (invoice, cents = 0) => {
    const converted = convertCurrencyCents(cents, invoice?.currency || displayCurrency, displayCurrency, ratesData);
    return fmtCurrency(converted, displayCurrency);
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  async function loadInvoices() {
    try {
      const out = await authedFetch("/api/invoices");
      setInvoices(out.invoices || []);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }

  async function markAsPaid(invoiceId) {
    try {
      const out = await authedFetch(`/api/invoices/${invoiceId}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "paid" }),
      });
      setInvoices((rows) => rows.map((inv) => inv.id === invoiceId ? { ...inv, ...(out.invoice || {}), status: "paid" } : inv));
      await loadInvoices();
      return true;
    } catch (e) {
      setErr(e.message);
      return false;
    }
  }

  if (loading) return <Card>Loading invoices...</Card>;

  return (
    <>
      {/* Create Invoice Button */}
      <Card style={{ marginBottom: 16 }}>
        <Button
          variant="primary"
          label="+ Create Manual Invoice"
          onClick={() => setShowCreateForm(true)}
          style={{ fontWeight: 600 }}
        />
      </Card>

      {/* Manual Invoice Creation Form */}
      {showCreateForm && (
        <ManualInvoiceForm
          authedFetch={authedFetch}
          setErr={setErr}
          settings={settings}
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            loadInvoices();
          }}
        />
      )}

      <Card>
        {invoices.length === 0 && !showCreateForm && (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>💰</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>No invoices yet</div>
            <div style={{ fontSize: 13 }}>Create your first invoice using the button above or through bookings.</div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {invoices.map(inv => (
            <article key={inv.id} onClick={() => setViewingInvoice(inv)} role="button" tabIndex={0} onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setViewingInvoice(inv);
              }
            }} style={{
              padding: 16,
              background: "var(--bg-soft)",
              border: "1px solid var(--border)",
              borderRadius: 12,
              cursor: "pointer"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: "var(--text)" }}>
                  {inv.number || `Invoice #${inv.id.slice(0, 8)}`}
                </div>
                <div style={{
                  padding: "4px 12px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  background: statusBadgeBg(inv.status),
                  color: statusBadgeFg(inv.status)
                }}>
                  {formatStatus(inv.status)}
                </div>
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
                {inv.customer?.full_name || inv.customer?.email} · {invoiceMoney(inv, inv.total_cents)} · {inv.method}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  variant="outline"
                  label="View details"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewingInvoice(inv);
                  }}
                  style={{ fontSize: 12, padding: "6px 12px" }}
                />
                <Button
                  variant="outline"
                  label="Share"
                  onClick={(e) => {
                    e.stopPropagation();
                    shareInvoice(inv, displayCurrency, ratesData);
                  }}
                  style={{ fontSize: 12, padding: "6px 12px" }}
                />
                {isPayableInvoice(inv.status) && (
                  <Button
                    variant="outline"
                    label="Mark as Paid"
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsPaid(inv.id);
                    }}
                    style={{ fontSize: 12, padding: "6px 12px" }}
                  />
                )}
              </div>
            </article>
          ))}
        </div>
      </Card>

      {viewingInvoice && (
        <InvoiceModal
          invoice={viewingInvoice}
          settings={settings}
          ratesData={ratesData}
          onClose={() => setViewingInvoice(null)}
          onMarkPaid={async () => {
            const ok = await markAsPaid(viewingInvoice.id);
            if (ok) setViewingInvoice(null);
          }}
        />
      )}
    </>
  );
}

/**
 * InvoiceModal - Professional invoice display with PDF export
 */
function InvoiceModal({ invoice, settings, ratesData, onClose, onMarkPaid }) {
  const invoiceRef = useRef(null);
  const displayCurrency = settings?.currency_code || invoice.currency || "USD";
  const money = (cents = 0) => {
    const converted = convertCurrencyCents(cents, invoice.currency || displayCurrency, displayCurrency, ratesData);
    return fmtCurrency(converted, displayCurrency);
  };

  function printInvoice() {
    window.print();
  }

  function downloadPDF() {
    // Use browser's print-to-PDF functionality
    // In a production app, you'd use a library like jsPDF or call a server endpoint
    window.print();
  }

  return (
    <>
      {/* Professional Print Stylesheet */}
      <style>{`
        @media print {
          /* Hide everything except the invoice */
          body * {
            visibility: hidden;
          }

          /* Show only the invoice content */
          .invoice-printable, .invoice-printable * {
            visibility: visible;
          }

          .invoice-printable {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white;
          }

          /* Hide action buttons in print */
          .no-print {
            display: none !important;
          }

          /* Page settings */
          @page {
            size: letter;
            margin: 0.5in;
          }

          /* Invoice styling for print */
          .invoice-printable {
            padding: 0;
            box-shadow: none;
            border-radius: 0;
            max-width: none;
          }

          /* Ensure clean breaks */
          .invoice-printable table {
            page-break-inside: avoid;
          }

          /* Better text rendering for print */
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>

      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16
      }} onClick={onClose}>
      <div ref={invoiceRef} onClick={(e) => e.stopPropagation()} className="invoice-printable" style={{
        background: "#fff",
        borderRadius: 12,
        maxWidth: 800,
        width: "100%",
        maxHeight: "90vh",
        overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
      }}>
        {/* Invoice Header - Actions */}
        <div className="no-print" style={{
          padding: "16px 24px",
          borderBottom: "1px solid #E5E7EB",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#F9FAFB"
        }}>
          <div style={{ fontWeight: 600, fontSize: 16, color: "#111827" }}>Invoice Details</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={printInvoice} style={{
              padding: "8px 16px",
              background: "#fff",
              border: "1px solid #D1D5DB",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              color: "#374151"
            }}>
              🖨️ Print
            </button>
            <button onClick={downloadPDF} style={{
              padding: "8px 16px",
              background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
              border: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              color: "#fff"
            }}>
              📄 Download PDF
            </button>
            <button onClick={() => shareInvoice(invoice, displayCurrency, ratesData)} style={{
              padding: "8px 16px",
              background: "#fff",
              border: "1px solid #D1D5DB",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              color: "#374151"
            }}>
              Share
            </button>
            <button onClick={onClose} style={{
              padding: "8px 12px",
              background: "#fff",
              border: "1px solid #D1D5DB",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              color: "#374151"
            }}>
              ✕
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div style={{ padding: "40px 48px" }}>
          {/* Business Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 40 }}>
            <div>
              <div style={{
                fontSize: 32,
                fontWeight: 700,
                fontFamily: "var(--font-display)",
                background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                marginBottom: 8
              }}>
                Oge Nail Studio
              </div>
              <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6 }}>
                123 Beauty Lane<br />
                New York, NY 10001<br />
                contact@ogenailstudio.com<br />
                (555) 123-4567
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#9CA3AF",
                letterSpacing: 1.5,
                marginBottom: 8
              }}>
                INVOICE
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
                #{invoice.id.slice(0, 8).toUpperCase()}
              </div>
              <div style={{
                display: "inline-block",
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                background: invoice.status === "paid" ? "rgba(50,160,80,0.16)" : "rgba(255,165,0,0.16)",
                color: invoice.status === "paid" ? "#1F6E36" : "#CC6600"
              }}>
                {formatStatus(invoice.status)}
              </div>
            </div>
          </div>

          {/* Customer & Date Info */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
            gap: 24,
            marginBottom: 40,
            padding: 24,
            background: "#F9FAFB",
            borderRadius: 8
          }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: 1.2, marginBottom: 8 }}>
                BILL TO
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
                {invoice.customer?.full_name || "Customer"}
              </div>
              <div style={{ fontSize: 13, color: "#6B7280" }}>
                {invoice.customer?.email}
              </div>
              {invoice.customer?.phone && (
                <div style={{ fontSize: 13, color: "#6B7280" }}>
                  {invoice.customer.phone}
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: 1.2, marginBottom: 8 }}>
                INVOICE DATE
              </div>
              <div style={{ fontSize: 13, color: "#374151", marginBottom: 12 }}>
                {new Date(invoice.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              {invoice.paid_at && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: 1.2, marginBottom: 8 }}>
                    PAID DATE
                  </div>
                  <div style={{ fontSize: 13, color: "#374151" }}>
                    {new Date(invoice.paid_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Line Items Table */}
          <div style={{ marginBottom: 40 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #E5E7EB" }}>
                  <th style={{
                    textAlign: "left",
                    padding: "12px 0",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#9CA3AF",
                    letterSpacing: 1.2
                  }}>
                    DESCRIPTION
                  </th>
                  <th style={{
                    textAlign: "right",
                    padding: "12px 0",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#9CA3AF",
                    letterSpacing: 1.2,
                    width: 120
                  }}>
                    AMOUNT
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoiceItems(invoice).map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #F3F4F6" }}>
                    <td style={{
                      padding: "16px 0",
                      fontSize: 14,
                      color: "#374151",
                      lineHeight: 1.5
                    }}>
                      {item.description}
                    </td>
                    <td style={{
                      padding: "16px 0",
                      fontSize: 14,
                      color: "#374151",
                      textAlign: "right",
                      fontWeight: 600
                    }}>
                      {money(item.amount_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Total */}
          <div style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "24px 0",
            borderTop: "2px solid #E5E7EB"
          }}>
            <div style={{ minWidth: 280 }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 16,
                fontSize: 14,
                color: "#6B7280"
              }}>
                <span>Subtotal:</span>
                <span style={{ fontWeight: 600 }}>{money(invoice.total_cents)}</span>
              </div>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 16,
                borderTop: "1px solid #E5E7EB",
                fontSize: 18,
                fontWeight: 700,
                color: "#111827"
              }}>
                <span>Total:</span>
                <span style={{
                  background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text"
                }}>
                  {money(invoice.total_cents)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div style={{
            padding: 20,
            background: "#F9FAFB",
            borderRadius: 8,
            marginBottom: 32
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: 1.2, marginBottom: 8 }}>
              PAYMENT METHOD
            </div>
            <div style={{ fontSize: 14, color: "#374151", fontWeight: 600 }}>
              {invoice.method === "cash" ? "💵 Cash" : invoice.method === "transfer" ? "↔ Transfer" : "💳 Card"}
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: 1.2, marginBottom: 8 }}>
                NOTES
              </div>
              <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6 }}>
                {invoice.notes}
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{
            paddingTop: 32,
            borderTop: "1px solid #E5E7EB",
            textAlign: "center"
          }}>
            <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 8 }}>
              Thank you for choosing Oge Nail Studio!
            </div>
            <div style={{ fontSize: 12, color: "#D1D5DB" }}>
              Questions? Email us at contact@ogenailstudio.com or call (555) 123-4567
            </div>
          </div>
        </div>

        {/* Action Footer */}
        {isPayableInvoice(invoice.status) && (
          <div className="no-print" style={{
            padding: "16px 24px",
            borderTop: "1px solid #E5E7EB",
            background: "#F9FAFB",
            display: "flex",
            justifyContent: "flex-end"
          }}>
            <button onClick={onMarkPaid} style={{
              padding: "10px 20px",
              background: "linear-gradient(135deg, #10B981, #059669)",
              border: "none",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              color: "#fff"
            }}>
              ✓ Mark as Paid
            </button>
          </div>
        )}
      </div>
      </div>
    </>
  );
}

/**
 * Editable row for a single location. Click "Edit" to inline-edit name and
 * address fields without a modal. Reactivate / archive flips `is_active`.
 */
function LocationRow({ location, onPatch, onDeactivate }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(location);
  const dirty = JSON.stringify(draft) !== JSON.stringify(location);

  function startEdit() { setDraft(location); setEditing(true); }
  function cancel()    { setDraft(location); setEditing(false); }
  async function save() {
    const patch = {};
    for (const k of ["name","address","city","state","postal_code","country","timezone"]) {
      if (draft[k] !== location[k]) patch[k] = draft[k] ?? null;
    }
    if (Object.keys(patch).length) await onPatch(patch);
    setEditing(false);
  }

  return (
    <article style={{
      background: location.is_active ? "var(--bg-elevated)" : "var(--bg-soft)",
      borderRadius: 12, padding: 14,
      border: location.is_active
        ? "1px solid var(--border)"
        : "1.5px dashed var(--border-strong)",
      opacity: location.is_active ? 1 : 0.7,
    }}>
      {editing ? (
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
          <Input label="Name"        value={draft.name || ""}        onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <Input label="Address"     value={draft.address || ""}     onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
          <Input label="City"        value={draft.city || ""}        onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
          <Input label="State"       value={draft.state || ""}       onChange={(e) => setDraft({ ...draft, state: e.target.value })} />
          <Input label="Postal"      value={draft.postal_code || ""} onChange={(e) => setDraft({ ...draft, postal_code: e.target.value })} />
          <Input label="Country"     value={draft.country || ""}     onChange={(e) => setDraft({ ...draft, country: e.target.value })} />
          <Input label="Timezone"    value={draft.timezone || ""}    onChange={(e) => setDraft({ ...draft, timezone: e.target.value })} />
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <Button type="button" label="Save"   variant="primary" onClick={save}   disabled={!dirty} />
            <Button type="button" label="Cancel" variant="outline" onClick={cancel} />
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
                {location.name}
              </div>
              {!location.is_active && (
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", padding: "2px 8px", borderRadius: 999, background: "var(--bg-soft-hover)", color: "var(--text-muted)" }}>
                  Archived
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
              {[location.address, location.city, location.state, location.postal_code, location.country].filter(Boolean).join(", ") || "No address on file"}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
              TZ: {location.timezone || "—"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button type="button" size="sm" variant="outline" label="Edit" onClick={startEdit} />
            {location.is_active ? (
              <Button type="button" size="sm" variant="danger" label="Archive" onClick={onDeactivate} />
            ) : (
              <Button type="button" size="sm" variant="primary" label="Reactivate" onClick={() => onPatch({ is_active: true })} />
            )}
          </div>
        </div>
      )}
    </article>
  );
}

// ── Exchange Rates Widget ─────────────────────────────────────────────────────
// Shows the studio's currency vs USD rate, day-over-day % change, a 30-day
// sparkline, and the last-updated timestamp. Follows standard mid-market
// daily-rate conventions (same as Bloomberg, XE, Wise, etc.).
function ExchangeRatesWidget({ ratesData, studioCurrency, refreshing, onRefresh }) {
  const code = (studioCurrency || "USD").toUpperCase();

  if (!ratesData) {
    return (
      <Card style={{ maxWidth: "100%" }}>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Loading exchange rates…</div>
      </Card>
    );
  }

  const { rates, changes, rateDate, source, trackedCurrencies, meta } = ratesData;

  // Studio currency rate (units per 1 USD)
  const studioRate   = rates?.[code] ?? 1;
  const studioChange = changes?.find((c) => c.target === code);
  const pctChange    = studioChange?.pct_change != null ? Number(studioChange.pct_change) : null;
  const isUp         = pctChange != null && pctChange > 0;
  const isDown       = pctChange != null && pctChange < 0;
  const changeColor  = isUp ? "#16A34A" : isDown ? "#DC2626" : "var(--text-muted)";
  const changeArrow  = isUp ? "▲" : isDown ? "▼" : "–";
  const sym          = CURRENCY_SYMBOLS[code] || code;

  // Build the full comparison table (all tracked currencies)
  const tableRows = (trackedCurrencies || [])
    .filter((t) => rates?.[t] != null)
    .map((target) => {
      const ch  = changes?.find((c) => c.target === target);
      const pct = ch?.pct_change != null ? Number(ch.pct_change) : null;
      return {
        target,
        name: meta?.[target]?.name ?? target,
        symbol: CURRENCY_SYMBOLS[target] || target,
        rate: Number(rates[target]),
        pctChange: pct,
        rateYesterday: ch?.rate_yesterday != null ? Number(ch.rate_yesterday) : null,
      };
    })
    .sort((a, b) => {
      // Studio currency first, then alphabetical
      if (a.target === code) return -1;
      if (b.target === code) return 1;
      return a.target.localeCompare(b.target);
    });

  // Sparkline from 30-day history (we store it in the changes view's fetched_at chain,
  // but for a quick visual we can approximate from what we have.
  // For the studio currency we fetch full history separately when the tab opens.
  // Here we show the key stats + table; the full chart is in AdvancedReports.)

  return (
    <Card style={{ maxWidth: "100%" }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <div>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", margin: 0 }}>
            Exchange Rates
          </h3>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
            Daily mid-market · Base: USD · {rateDate ?? "—"} · {source?.split("(")[0]?.trim()}
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          style={{
            fontSize: 12, padding: "6px 14px", borderRadius: 8,
            border: "1px solid var(--border)", background: "var(--bg-soft)",
            color: "var(--text)", cursor: refreshing ? "default" : "pointer", opacity: refreshing ? 0.6 : 1,
          }}
        >
          {refreshing ? "Refreshing…" : "↻ Refresh rates"}
        </button>
      </div>

      {/* Studio currency hero tile */}
      {code !== "USD" && (
        <div style={{
          background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
          borderRadius: 14, padding: "18px 20px", marginBottom: 16, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", opacity: 0.75 }}>
              Studio currency — {code}
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, fontFamily: "var(--font-display)", marginTop: 4 }}>
              {sym}{studioRate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
            </div>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>per 1 USD</div>
          </div>
          <div style={{ textAlign: "right" }}>
            {pctChange != null ? (
              <>
                <div style={{
                  fontSize: 24, fontWeight: 700, color: isUp ? "#86EFAC" : isDown ? "#FCA5A5" : "rgba(255,255,255,0.7)",
                }}>
                  {changeArrow} {Math.abs(pctChange).toFixed(3)}%
                </div>
                <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>vs yesterday</div>
                {studioChange?.rate_yesterday != null && (
                  <div style={{ fontSize: 11, opacity: 0.6, marginTop: 1 }}>
                    Prev: {sym}{Number(studioChange.rate_yesterday).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 13, opacity: 0.6 }}>No prior data</div>
            )}
          </div>
        </div>
      )}

      {/* Full rates table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--border)" }}>
              <th style={{ textAlign: "left",  padding: "6px 8px", color: "var(--text-muted)", fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>Currency</th>
              <th style={{ textAlign: "right", padding: "6px 8px", color: "var(--text-muted)", fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>Rate (per USD)</th>
              <th style={{ textAlign: "right", padding: "6px 8px", color: "var(--text-muted)", fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>Yesterday</th>
              <th style={{ textAlign: "right", padding: "6px 8px", color: "var(--text-muted)", fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>Change</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row) => {
              const isStudio = row.target === code;
              const rColor   = row.pctChange > 0 ? "#16A34A" : row.pctChange < 0 ? "#DC2626" : "var(--text-muted)";
              const rArrow   = row.pctChange > 0 ? "▲" : row.pctChange < 0 ? "▼" : "–";
              return (
                <tr
                  key={row.target}
                  style={{
                    borderBottom: "1px solid var(--border)",
                    background: isStudio ? "var(--bg-soft)" : "transparent",
                  }}
                >
                  <td style={{ padding: "8px 8px", color: "var(--text)", fontWeight: isStudio ? 700 : 400 }}>
                    <span style={{ fontWeight: 700, marginRight: 6 }}>{row.target}</span>
                    <span style={{ color: "var(--text-muted)", fontSize: 12 }}>{row.name}</span>
                    {isStudio && (
                      <span style={{
                        marginLeft: 8, fontSize: 10, padding: "2px 7px", borderRadius: 999,
                        background: "var(--mauve)", color: "#fff", fontWeight: 700,
                      }}>studio</span>
                    )}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 600, color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>
                    {row.symbol}{row.rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "right", color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>
                    {row.rateYesterday != null
                      ? `${row.symbol}${row.rateYesterday.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
                      : "—"}
                  </td>
                  <td style={{ padding: "8px 8px", textAlign: "right", color: rColor, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                    {row.pctChange != null
                      ? `${rArrow} ${Math.abs(row.pctChange).toFixed(3)}%`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: "var(--text-muted)" }}>
        Mid-market rates are indicative only. Actual payment processing rates may differ. Data from {source?.split("(")[0]?.trim() ?? "open.er-api.com"}.
      </div>
    </Card>
  );
}

function RStat({ label, value, accent }) {
  return (
    <div style={{
      background: accent ? "linear-gradient(135deg, var(--mauve), var(--mauve-deep))" : "var(--bg-soft)",
      color: accent ? "#fff" : "var(--text)",
      borderRadius: 12, padding: "12px 14px",
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "var(--font-display)", marginTop: 4 }}>{value}</div>
    </div>
  );
}

function AdvancedReports({ analytics, currencyCode }) {
  const accounting = analytics.accounting || {};
  const operations = analytics.operations || {};
  const conv       = analytics.conversionMeta;
  const showUsd    = conv && conv.target !== "USD" && conv.rateUsdPerTarget;
  const money      = (cents = 0) => fmtCurrency(cents, currencyCode);
  // Show "₦4,200 ≈ $2.80" when studio currency ≠ USD
  const moneyWithUsd = (nativeCents = 0, usdCents) => {
    const native = money(nativeCents);
    if (!showUsd || usdCents == null) return native;
    return `${native} ≈ ${fmtCurrency(usdCents, "USD")}`;
  };
  const pct = (value = 0) => `${Math.round(Number(value || 0) * 100)}%`;
  return (
    <Card style={{ maxWidth: "100%", border: "1px solid var(--border-strong)", background: "linear-gradient(135deg, rgba(196,135,154,0.10), var(--bg-elevated))" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", margin: 0, fontSize: 24 }}>
            Accounting intelligence
          </h3>
          <p style={{ color: "var(--text-muted)", margin: "6px 0 0", fontSize: 13 }}>
            Revenue, receivables, operational efficiency, design demand, and data-driven recommendations.
          </p>
          {showUsd && (
            <p style={{ color: "var(--text-muted)", margin: "4px 0 0", fontSize: 11 }}>
              USD equivalents (≈) use mid-market rate: 1 USD = {conv.rateUsdPerTarget?.toFixed(4)} {conv.target} · {conv.rateDate} · {conv.source?.split("(")[0]?.trim()}
            </p>
          )}
        </div>
        <span style={{ padding: "6px 12px", borderRadius: 999, background: "rgba(201,168,76,0.16)", color: "var(--text-gold)", fontSize: 12, fontWeight: 800 }}>
          Last {analytics.sinceDays || 30} days
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <RStat label={showUsd ? `Earned (${currencyCode})` : "Earned"} value={money(accounting.earnedCents)} accent />
        {showUsd && <RStat label="Earned (USD ≈)" value={fmtCurrency(accounting.earnedUsdCents, "USD")} />}
        <RStat label="Completed revenue" value={moneyWithUsd(accounting.completedRevenueCents, accounting.completedRevenueUsdCents)} />
        <RStat label="Booked value"    value={moneyWithUsd(accounting.bookedValueCents, accounting.bookedValueUsdCents)} />
        <RStat label="Gross bookings"  value={moneyWithUsd(accounting.grossBookingCents, accounting.grossBookingUsdCents)} />
        <RStat label="Tips"            value={moneyWithUsd(accounting.bookingTipCents, accounting.bookingTipUsdCents)} />
        <RStat label="Discounts"       value={moneyWithUsd(accounting.bookingDiscountCents, accounting.bookingDiscountUsdCents)} />
        <RStat label="Avg completed"   value={moneyWithUsd(accounting.averageCompletedBookingCents, accounting.averageCompletedBookingUsdCents)} />
        <RStat label="Receivable"      value={moneyWithUsd(accounting.receivableCents,  accounting.receivableUsdCents)} />
        <RStat label="Deposits"        value={moneyWithUsd(accounting.depositCents,     accounting.depositUsdCents)} />
        <RStat label="Card collected"  value={moneyWithUsd(accounting.cardCollectedCents, accounting.cardCollectedUsdCents)} />
        <RStat label="Failed/refunded" value={moneyWithUsd(accounting.failedCents,      accounting.failedUsdCents)} />
        <RStat label="Completion"      value={pct(operations.completionRate)} />
        <RStat label="Conversion"      value={pct(operations.conversionRate)} />
        <RStat label="Completed"       value={operations.completedCount || 0} />
        <RStat label="No-shows"        value={operations.noShowCount || 0} />
        <RStat label="Cancelled"       value={operations.cancelledCount || 0} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, marginTop: 18 }}>
        <ReportPanel title="Design demand">
          {(analytics.popularDesigns || []).slice(0, 6).map((d) => (
            <ReportRow key={d.name} left={d.name} right={`${d.count} signals`} sub={[d.category, d.technique, d.difficulty].filter(Boolean).join(" · ")} />
          ))}
          {!analytics.popularDesigns?.length && <EmptyReportText>No design demand data yet.</EmptyReportText>}
        </ReportPanel>

        <ReportPanel title="Category mix">
          {(analytics.popularCategories || []).slice(0, 6).map((c) => (
            <ReportRow key={c.category} left={c.category} right={c.count} />
          ))}
          {!analytics.popularCategories?.length && <EmptyReportText>No category data yet.</EmptyReportText>}
        </ReportPanel>

        <ReportPanel title="Cost / benefit by tech">
          {(analytics.technicianEconomics || []).slice(0, 6).map((t) => (
            <ReportRow
              key={t.technician_id}
              left={t.name}
              right={moneyWithUsd(t.benefitCents, t.benefitUsdCents)}
              sub={`${t.completed}/${t.bookings} completed · labor ${money(t.estimatedLaborCents)} · gross ${money(t.grossCents)}${showUsd && t.grossUsdCents != null ? ` ≈ ${fmtCurrency(t.grossUsdCents, "USD")}` : ""}${t.avgRating ? ` · ${t.avgRating.toFixed(1)}★` : ""}`}
            />
          ))}
          {!analytics.technicianEconomics?.length && <EmptyReportText>No technician economics yet.</EmptyReportText>}
        </ReportPanel>
      </div>

      <ReportPanel title="Expert recommendations" style={{ marginTop: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
          {(analytics.suggestions || []).map((s, i) => (
            <article key={`${s.title}-${i}`} style={{ border: "1px solid var(--border)", borderRadius: 14, padding: 14, background: "var(--bg-elevated)" }}>
              <div style={{ fontSize: 11, color: "var(--text-gold)", letterSpacing: 1.1, textTransform: "uppercase", fontWeight: 900 }}>{s.impact}</div>
              <div style={{ color: "var(--text)", fontWeight: 800, marginTop: 5 }}>{s.title}</div>
              <p style={{ color: "var(--text-muted)", fontSize: 13, lineHeight: 1.6, margin: "8px 0 0" }}>{s.body}</p>
            </article>
          ))}
        </div>
      </ReportPanel>
    </Card>
  );
}

function ReportPanel({ title, children, style }) {
  return (
    <section style={{ background: "var(--bg-soft)", border: "1px solid var(--border)", borderRadius: 16, padding: 14, ...style }}>
      <div style={{ color: "var(--text)", fontWeight: 900, marginBottom: 10 }}>{title}</div>
      <div style={{ display: "grid", gap: 8 }}>{children}</div>
    </section>
  );
}

function ReportRow({ left, right, sub }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, borderBottom: "1px solid var(--border)", paddingBottom: 8 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: "var(--text)", fontWeight: 700 }}>{left}</div>
        {sub && <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ color: "var(--text-accent)", fontWeight: 900, whiteSpace: "nowrap" }}>{right}</div>
    </div>
  );
}

function EmptyReportText({ children }) {
  return <div style={{ color: "var(--text-muted)", fontSize: 13 }}>{children}</div>;
}

// ════════════════════════════════════════════════════════════════════════
// Studio Settings Form Component
// ════════════════════════════════════════════════════════════════════════
const DEFAULT_DESIGN_TIERS = [
  { label: "Simple",   price_cents: 2000000 },
  { label: "Standard", price_cents: 3000000 },
  { label: "Premium",  price_cents: 4500000 },
];

function PricingSettingsForm({ settings, authedFetch, setSettings, setErr }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [tierDraft, setTierDraft] = useState([]); // [{ label, price }] price as display string
  const [saving, setSaving] = useState(false);
  const sym = CURRENCY_SYMBOLS[settings.currency_code] || settings.currency_code || '$';
  const tiers = Array.isArray(settings.design_price_tiers) && settings.design_price_tiers.length
    ? settings.design_price_tiers
    : DEFAULT_DESIGN_TIERS;

  const startEdit = () => {
    // Store as display strings (dollar/naira amounts) so the user can type
    // freely without the field resetting. Convert to cents only on save.
    setDraft({
      subscription_silver_cents: ((settings.subscription_silver_cents ?? 1900) / 100).toFixed(2),
      subscription_gold_cents:   ((settings.subscription_gold_cents   ?? 4900) / 100).toFixed(2),
      ai_design_price_cents:     ((settings.ai_design_price_cents     ?? 0)    / 100).toFixed(2),
      default_booking_price_cents: ((settings.default_booking_price_cents ?? 0) / 100).toFixed(2),
    });
    setTierDraft(tiers.map((t) => ({ label: t.label, price: (t.price_cents / 100).toFixed(2) })));
    setEditing(true);
  };
  const cancel = () => setEditing(false);
  const save = async () => {
    setSaving(true);
    try {
      // Convert display strings back to cents integers for the API
      const payload = {};
      for (const k of Object.keys(draft)) {
        payload[k] = Math.round(parseFloat(draft[k] || "0") * 100);
      }
      // Design price tiers — drop blank rows, coerce to cents.
      const cleanedTiers = tierDraft
        .map((t) => ({ label: (t.label || "").trim(), price_cents: Math.round(parseFloat(t.price || "0") * 100) }))
        .filter((t) => t.label && Number.isFinite(t.price_cents) && t.price_cents >= 0);
      if (cleanedTiers.length === 0) {
        setErr("Add at least one design price tier."); setSaving(false); return;
      }
      payload.design_price_tiers = cleanedTiers;
      const resp = await authedFetch('/api/admin/settings', { method: 'PATCH', body: JSON.stringify(payload) });
      setSettings(resp.settings);
      setEditing(false);
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };
  const setTier = (i, key, val) => setTierDraft((prev) => prev.map((t, idx) => idx === i ? { ...t, [key]: val } : t));
  const addTier = () => setTierDraft((prev) => [...prev, { label: "", price: "0.00" }]);
  const removeTier = (i) => setTierDraft((prev) => prev.filter((_, idx) => idx !== i));

  const field = (key, label, note) => {
    const centsVal = settings[key] ?? 0;
    return (
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 6 }}>{label}</div>
        {editing ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{sym}</span>
            <input
              type="text"
              inputMode="decimal"
              value={draft[key]}
              onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
              onBlur={(e) => {
                // Normalise to 2 dp when the field loses focus
                const n = parseFloat(e.target.value);
                setDraft({ ...draft, [key]: isNaN(n) ? "0.00" : n.toFixed(2) });
              }}
              style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: "1.5px solid var(--border-strong)", background: "var(--bg)", color: "var(--text)", fontSize: 15, fontWeight: 700 }}
            />
          </div>
        ) : (
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text)" }}>
            {centsVal === 0 ? <span style={{ color: "var(--text-muted)", fontSize: 14 }}>Free</span> : fmtCurrency(centsVal, settings.currency_code)}
          </div>
        )}
        {note && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{note}</div>}
      </div>
    );
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 16 }}>
        {field("subscription_silver_cents", "🥈 Silver Membership / quarter", "10% off bookings · Priority slots")}
        {field("subscription_gold_cents",   "🥇 Gold Membership / quarter",   "20% off · Dedicated tech · Member designs")}
        {field("ai_design_price_cents",     "🤖 AI Design Generation", "Per request (0 = free within quota)")}
        {field("default_booking_price_cents", "📅 Default Booking Price", "Pre-filled when admin books for a customer")}
      </div>

      {/* Design price tiers — the price shown on each generated design is mapped
          from its difficulty (Simple / Standard / Premium) to one of these. */}
      <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 4 }}>💅 Design Price Tiers</div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
          Each AI-generated design is priced by mapping its difficulty to one of these tiers. Customers see this price (not an AI guess).
        </div>
        {editing ? (
          <div style={{ display: "grid", gap: 8 }}>
            {tierDraft.map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="text" placeholder="Tier name (e.g. Premium)"
                  value={t.label}
                  onChange={(e) => setTier(i, "label", e.target.value)}
                  style={{ flex: 1, padding: "9px 12px", borderRadius: 10, border: "1.5px solid var(--border-strong)", background: "var(--bg)", color: "var(--text)", fontSize: 14, fontWeight: 600 }}
                />
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>{sym}</span>
                <input
                  type="text" inputMode="decimal"
                  value={t.price}
                  onChange={(e) => setTier(i, "price", e.target.value)}
                  onBlur={(e) => { const n = parseFloat(e.target.value); setTier(i, "price", isNaN(n) ? "0.00" : n.toFixed(2)); }}
                  style={{ width: 130, padding: "9px 12px", borderRadius: 10, border: "1.5px solid var(--border-strong)", background: "var(--bg)", color: "var(--text)", fontSize: 14, fontWeight: 700 }}
                />
                <button type="button" onClick={() => removeTier(i)} aria-label="Remove tier"
                  style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid var(--border-strong)", background: "var(--bg-soft)", color: "var(--text-muted)", cursor: "pointer", fontSize: 16 }}>×</button>
              </div>
            ))}
            <div><Button type="button" label="+ Add tier" variant="outline" onClick={addTier} style={{ fontSize: 12, padding: "7px 12px" }} /></div>
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {tiers.map((t, i) => (
              <div key={i} style={{ background: "var(--bg-soft)", border: "1px solid var(--border)", borderRadius: 12, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase" }}>{t.label}</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)", marginTop: 2 }}>{fmtCurrency(t.price_cents, settings.currency_code)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        {editing ? (
          <>
            <Button type="button" label={saving ? "Saving…" : "Save Prices"} variant="primary" onClick={save} disabled={saving} />
            <Button type="button" label="Cancel" variant="outline" onClick={cancel} disabled={saving} />
          </>
        ) : (
          <Button type="button" label="Edit Pricing" variant="primary" onClick={startEdit} />
        )}
      </div>
    </div>
  );
}

function StudioSettingsForm({ settings, authedFetch, setSettings, setErr }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);

  // Country options (common countries for nail studios)
  const countries = [
    { code: 'US', name: 'United States', currency: 'USD' },
    { code: 'CA', name: 'Canada', currency: 'CAD' },
    { code: 'GB', name: 'United Kingdom', currency: 'GBP' },
    { code: 'NG', name: 'Nigeria', currency: 'NGN' },
    { code: 'GH', name: 'Ghana', currency: 'GHS' },
    { code: 'ZA', name: 'South Africa', currency: 'ZAR' },
    { code: 'KE', name: 'Kenya', currency: 'KES' },
    { code: 'AU', name: 'Australia', currency: 'AUD' },
    { code: 'NZ', name: 'New Zealand', currency: 'NZD' },
    { code: 'DE', name: 'Germany', currency: 'EUR' },
    { code: 'FR', name: 'France', currency: 'EUR' },
    { code: 'ES', name: 'Spain', currency: 'EUR' },
    { code: 'IT', name: 'Italy', currency: 'EUR' },
    { code: 'AE', name: 'United Arab Emirates', currency: 'AED' },
    { code: 'IN', name: 'India', currency: 'INR' },
    { code: 'SG', name: 'Singapore', currency: 'SGD' },
    { code: 'MY', name: 'Malaysia', currency: 'MYR' },
    { code: 'JP', name: 'Japan', currency: 'JPY' },
    { code: 'BR', name: 'Brazil', currency: 'BRL' },
    { code: 'MX', name: 'Mexico', currency: 'MXN' },
  ];

  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    const country = countries.find(c => c.code === countryCode);
    setDraft({
      ...draft,
      country_code: countryCode,
      currency_code: country ? country.currency : 'USD'
    });
  };

  const startEdit = () => {
    setDraft(settings);
    setEditing(true);
  };

  const cancel = () => {
    setDraft(settings);
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const resp = await authedFetch('/api/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify(draft)
      });
      setSettings(resp.settings);
      setEditing(false);
      setErr('');
    } catch (e) {
      setErr(e.message);
    }
    setSaving(false);
  };

  const currentCountry = countries.find(c => c.code === (editing ? draft.country_code : settings.country_code));
  const currencySymbol = CURRENCY_SYMBOLS;

  return (
    <div>
      {!editing ? (
        <div style={{ display: "grid", gap: 20 }}>
          {/* Display current settings */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
            gap: 16
          }}>
            <div style={{
              background: "var(--bg-soft)",
              borderRadius: 12,
              padding: 16,
              border: "1px solid var(--border)"
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
                Country
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
                {currentCountry ? currentCountry.name : settings.country_code}
              </div>
            </div>

            <div style={{
              background: "var(--bg-soft)",
              borderRadius: 12,
              padding: 16,
              border: "1px solid var(--border)"
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
                Currency
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
                {currencySymbol[settings.currency_code] || ''} {settings.currency_code}
              </div>
            </div>

            <div style={{
              background: "var(--bg-soft)",
              borderRadius: 12,
              padding: 16,
              border: "1px solid var(--border)"
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
                Business Name
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
                {settings.business_name || 'Oge Nail Studio'}
              </div>
            </div>

            <div style={{
              background: "var(--bg-soft)",
              borderRadius: 12,
              padding: 16,
              border: "1px solid var(--border)"
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
                Timezone
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
                {settings.timezone || 'America/New_York'}
              </div>
            </div>

            <div style={{
              background: "var(--bg-soft)",
              borderRadius: 12,
              padding: 16,
              border: "1px solid var(--border)"
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
                Deferred Payment Deposit
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>
                {settings.booking_deposit_enabled ? `${settings.booking_deposit_percent || 30}% required` : 'Off'}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <Button type="button" label="Edit Settings" variant="primary" onClick={startEdit} />
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 12 }}>
            {/* Country selector */}
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:1.4, color:"var(--accent)", marginBottom:6 }}>
                Country *
              </label>
              <select
                value={draft.country_code || 'US'}
                onChange={handleCountryChange}
                style={{
                  width:"100%",
                  padding:"13px 16px",
                  borderRadius:12,
                  border:"1.5px solid var(--border-strong)",
                  background:"var(--bg-elevated)",
                  color:"var(--text)",
                  fontSize:14,
                  fontWeight:600
                }}
              >
                {countries.map(c => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Currency display (auto-filled) */}
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:1.4, color:"var(--accent)", marginBottom:6 }}>
                Currency (Auto-set)
              </label>
              <div style={{
                padding:"13px 16px",
                borderRadius:12,
                border:"1.5px solid var(--border)",
                background:"var(--bg-soft)",
                color:"var(--text-muted)",
                fontSize:14,
                fontWeight:600
              }}>
                {currencySymbol[draft.currency_code] || ''} {draft.currency_code}
              </div>
            </div>

            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:1.4, color:"var(--accent)", marginBottom:6 }}>
                Cash / Transfer Deposit
              </label>
              <button
                type="button"
                onClick={() => setDraft({ ...draft, booking_deposit_enabled: !draft.booking_deposit_enabled })}
                style={{
                  width:"100%",
                  padding:"13px 16px",
                  borderRadius:12,
                  border:`1.5px solid ${draft.booking_deposit_enabled ? "var(--accent)" : "var(--border)"}`,
                  background:draft.booking_deposit_enabled ? "rgba(196,135,154,0.14)" : "var(--bg-soft)",
                  color:"var(--text)",
                  fontSize:14,
                  fontWeight:700,
                  textAlign:"left",
                  cursor:"pointer"
                }}
              >
                {draft.booking_deposit_enabled ? "On" : "Off"}
              </button>
            </div>

            {draft.booking_deposit_enabled && (
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:1.4, color:"var(--accent)", marginBottom:6 }}>
                  Deposit Percent
                </label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[10, 20, 30].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setDraft({ ...draft, booking_deposit_percent: p })}
                      style={{
                        padding: "10px 14px",
                        borderRadius: 999,
                        border: `1.5px solid ${Number(draft.booking_deposit_percent) === p ? "var(--accent)" : "var(--border)"}`,
                        background: Number(draft.booking_deposit_percent) === p ? "var(--accent)" : "var(--bg-soft)",
                        color: Number(draft.booking_deposit_percent) === p ? "#fff" : "var(--text)",
                        fontWeight: 800,
                        cursor: "pointer"
                      }}
                    >
                      {p}%
                    </button>
                  ))}
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={draft.booking_deposit_percent ?? 30}
                    onChange={(e) => setDraft({ ...draft, booking_deposit_percent: e.target.value })}
                    style={{
                      width: 110,
                      padding:"10px 14px",
                      borderRadius:999,
                      border:"1.5px solid var(--border)",
                      background:"var(--bg-elevated)",
                      color:"var(--text)",
                      fontSize:14,
                      fontWeight:700
                    }}
                    aria-label="Custom deposit percent"
                  />
                </div>
              </div>
            )}

            <Input
              label="Business Name"
              value={draft.business_name || ''}
              onChange={(e) => setDraft({ ...draft, business_name: e.target.value })}
            />

            <Input
              label="Timezone"
              value={draft.timezone || ''}
              onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
              placeholder="America/New_York"
            />
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
            gap: 12
          }}>
            <Input
              label="Business Email"
              type="email"
              value={draft.business_email || ''}
              onChange={(e) => setDraft({ ...draft, business_email: e.target.value })}
            />

            <Input
              label="Business Phone"
              type="tel"
              value={draft.business_phone || ''}
              onChange={(e) => setDraft({ ...draft, business_phone: e.target.value })}
            />

            <Input
              label="Business Address"
              value={draft.business_address || ''}
              onChange={(e) => setDraft({ ...draft, business_address: e.target.value })}
            />
          </div>

          <div style={{
            padding: 12,
            background: "rgba(196,135,154,0.08)",
            borderRadius: 10,
            border: "1px solid rgba(196,135,154,0.2)"
          }}>
            <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.6 }}>
              <strong>Note:</strong> Changing the country will automatically update the currency.
              This affects how prices are displayed in invoices and payment forms.
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <Button
              type="button"
              label={saving ? "Saving..." : "Save Changes"}
              variant="primary"
              onClick={save}
              disabled={saving}
              loading={saving}
            />
            <Button
              type="button"
              label="Cancel"
              variant="outline"
              onClick={cancel}
              disabled={saving}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * AdminGalleryUpload — lets admin upload real nail-work photos to the public gallery.
 * Photos appear in the Gallery "Real Work" tab for all users to see.
 */
function AdminGalleryUpload({ authedFetch, setErr }) {
  const [uploads, setUploads] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState("");
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { loadGallery(); }, []);

  async function loadGallery() {
    setLoadingGallery(true);
    try {
      const out = await authedFetch("/api/gallery?type=uploaded&limit=30");
      setUploads(out.uploads || []);
    } catch (e) {
      setErr(e.message);
    }
    setLoadingGallery(false);
  }

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }

  async function uploadViaAuthedFetch() {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("image", file);
      if (description.trim()) form.append("description", description.trim());

      await authedFetch("/api/gallery/upload", { method: "POST", body: form });
      setFile(null);
      setPreview(null);
      setDescription("");
      if (fileRef.current) fileRef.current.value = "";
      setErr("");
      await loadGallery();
    } catch (e) {
      setErr(e.message);
    }
    setUploading(false);
  }

  async function deleteUpload(id) {
    if (!confirm("Delete this photo from the gallery?")) return;
    try {
      await authedFetch(`/api/gallery/${id}`, { method: "DELETE" });
      setUploads(prev => prev.filter(u => u.id !== id));
    } catch (e) {
      setErr(e.message);
    }
  }

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {/* Upload form */}
      <Card>
        <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 14 }}>
          Upload to Public Gallery
        </h3>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
          Photos uploaded here appear in the gallery for all customers to browse as inspiration.
        </p>

        {/* File input */}
        <div
          onClick={() => fileRef.current?.click()}
          style={{
            border: `2px dashed ${preview ? "var(--mauve)" : "var(--border-strong)"}`,
            borderRadius: 14,
            padding: preview ? 0 : 32,
            textAlign: "center",
            cursor: "pointer",
            background: preview ? "transparent" : "var(--bg-soft)",
            overflow: "hidden",
            marginBottom: 14,
            transition: "border-color 0.2s"
          }}
        >
          {preview ? (
            <img src={preview} alt="Preview" style={{ width: "100%", maxHeight: 320, objectFit: "cover", display: "block" }} />
          ) : (
            <>
              <div style={{ fontSize: 40, marginBottom: 10 }}>📸</div>
              <div style={{ fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>Click to choose photo</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>JPG, PNG, WebP · max 10 MB</div>
            </>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />

        {file && (
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>
                Caption (optional)
              </label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Gel ombre with gold foil details"
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: "1.5px solid var(--border-strong)",
                  background: "var(--bg)",
                  color: "var(--text)",
                  fontSize: 14
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Button
                label={uploading ? "Uploading…" : "Upload to Gallery"}
                variant="primary"
                onClick={uploadViaAuthedFetch}
                disabled={uploading}
                loading={uploading}
              />
              <Button
                label="Cancel"
                variant="outline"
                onClick={() => { setFile(null); setPreview(null); setDescription(""); if (fileRef.current) fileRef.current.value = ""; }}
              />
            </div>
          </>
        )}
      </Card>

      {/* Existing uploads */}
      <Card>
        <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 14 }}>
          Gallery Photos ({uploads.length})
        </h3>
        {loadingGallery ? (
          <div style={{ textAlign: "center", padding: 24, color: "var(--text-muted)" }}>Loading…</div>
        ) : uploads.length === 0 ? (
          <div style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
            No gallery photos yet — upload the first one above!
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
            {uploads.map(u => (
              <div key={u.id} style={{ position: "relative", borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)" }}>
                <img
                  src={u.thumbnail_url || u.image_url}
                  alt={u.description || "Gallery photo"}
                  style={{ width: "100%", aspectRatio: "1", objectFit: "cover", display: "block" }}
                />
                {u.description && (
                  <div style={{
                    padding: "6px 8px",
                    fontSize: 11,
                    color: "var(--text-muted)",
                    background: "var(--bg-elevated)",
                    lineHeight: 1.4
                  }}>
                    {u.description}
                  </div>
                )}
                <button
                  onClick={() => deleteUpload(u.id)}
                  style={{
                    position: "absolute", top: 6, right: 6,
                    width: 26, height: 26, borderRadius: "50%",
                    background: "rgba(0,0,0,0.55)", color: "#fff",
                    border: "none", cursor: "pointer", fontSize: 13,
                    display: "flex", alignItems: "center", justifyContent: "center"
                  }}
                  title="Delete photo"
                >
                  ×
                </button>
                <div style={{
                  position: "absolute", bottom: u.description ? 30 : 6, left: 6,
                  fontSize: 10, fontWeight: 700,
                  background: "rgba(0,0,0,0.5)", color: "#fff",
                  padding: "2px 6px", borderRadius: 4
                }}>
                  👁 {u.view_count || 0}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
